/* ============================================================
   tournaments.js — Modul de management de turnee (TurnOrder).
   Scripturi clasice (FĂRĂ import/export), scope global partajat.
   Se încarcă DUPĂ supabase-integration.js (vezi ordinea sacră §4.A):
   ... supabase-integration -> profile -> social -> tournaments -> feedback.

   Acest batch (2b) conține DOAR motoarele PURE de formate + self-test.
   NU cablează UI (tab/butoane) și NU execută nimic la încărcare —
   doar definește funcții globale. Scrierea în DB se face exclusiv prin
   RPC-uri SECURITY DEFINER (tournament_apply_pairing etc.), cablate în
   batch-urile următoare. Totul pe ID de profil (§23.1).

   Namespace ușor: expunem un obiect global `TO_TOURN` cu motoarele,
   plus alias-uri window.* pentru apel direct din handlere viitoare.
   ============================================================ */

(function () {
    'use strict';
    /* v9 8.3: fișierul se încarcă LA CERERE (TO_CHAT.ensureModule / useModule, KB §75.4):
       la primul tab „Turnee" sau la primul profil cu statistici de turneu — nu mai e în
       index.html (−92,9 kB minificat la pornire). Ștampila o pune build-cache.js;
       încărcătorul refuză o versiune amestecată după un deploy.
       EN: lazy-loaded; release stamp checked by the loader. */
    var STAMP = 'turnorder-3f352d21de';

    /* Logger defensiv: refolosește logError global dacă există, altfel no-op. */
    function _tlog(ctx, err) {
        try { if (typeof window.logError === 'function') { window.logError(ctx, err); return; } } catch (e) {}
        try { console.warn('[tournaments] ' + ctx, err); } catch (e) {}
    }

    /* ---------- Utilitare pure ---------- */

    /* Fisher–Yates cu sămânță opțională (determinist pentru teste).
       Nu mutăm array-ul primit; întoarcem o copie amestecată. */
    function _shuffle(arr, seedFn) {
        var a = arr.slice();
        var rnd = (typeof seedFn === 'function') ? seedFn : Math.random;
        for (var i = a.length - 1; i > 0; i--) {
            var j = Math.floor(rnd() * (i + 1));
            var t = a[i]; a[i] = a[j]; a[j] = t;
        }
        return a;
    }

    /* Generator determinist mic (mulberry32) — folosit doar când vrem
       rezultate reproductibile (ex. în self-test sau la seeding fix). */
    function _mulberry32(seed) {
        var s = seed >>> 0;
        return function () {
            s |= 0; s = (s + 0x6D2B79F5) | 0;
            var t = Math.imul(s ^ (s >>> 15), 1 | s);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    /* ============================================================
       ROUND ROBIN — 1v1 (2 locuri/masă), metoda cercului.
       Fiecare jucător întâlnește pe fiecare exact o dată (legs=1)
       sau tur-retur (legs=2). Impar => un „bye" pe rundă (rotativ).
       Întoarce structura standard acceptată de tournament_apply_pairing:
       [{ round_no, label, tables:[{ table_no, seats:[{seat_no,profile_id|null,is_bye}] }] }]
       ============================================================ */
    function generateRoundRobin1v1(playerIds, opts) {
        opts = opts || {};
        var legs = (opts.legs === 2) ? 2 : 1;
        var ids = (playerIds || []).slice();

        if (ids.length < 2) return [];

        // Impar => adăugăm un pseudo-jucător „BYE" (null) ca să egalizăm.
        var hasBye = (ids.length % 2 === 1);
        if (hasBye) ids.push(null);

        var n = ids.length;
        var roundsPerLeg = n - 1;
        var half = n / 2;

        // Fixăm primul jucator, restul se rotesc (circle method).
        var arr = ids.slice();
        var rounds = [];
        var roundNo = 0;

        for (var leg = 0; leg < legs; leg++) {
            // repornim rotația de la aranjamentul inițial la fiecare leg
            var wheel = ids.slice();
            for (var r = 0; r < roundsPerLeg; r++) {
                roundNo++;
                var tables = [];
                var tableNo = 0;
                for (var i = 0; i < half; i++) {
                    var p1 = wheel[i];
                    var p2 = wheel[n - 1 - i];
                    // la tur-retur alternăm gazda pentru echitate (cosmetic la board games)
                    if (leg === 1) { var tmp = p1; p1 = p2; p2 = tmp; }
                    // sar peste „masa" fictivă dintre doi null (nu se întâmplă la un singur bye)
                    if (p1 === null && p2 === null) continue;
                    tableNo++;
                    var seats;
                    if (p1 === null || p2 === null) {
                        // bye: jucatorul real ia locul 1, locul 2 e gol
                        var real = (p1 === null) ? p2 : p1;
                        seats = [
                            { seat_no: 1, profile_id: real, is_bye: false },
                            { seat_no: 2, profile_id: null, is_bye: true }
                        ];
                    } else {
                        seats = [
                            { seat_no: 1, profile_id: p1, is_bye: false },
                            { seat_no: 2, profile_id: p2, is_bye: false }
                        ];
                    }
                    tables.push({ table_no: tableNo, seats: seats });
                }
                rounds.push({ round_no: roundNo, label: null, tables: tables });
                // rotație: fixăm elementul 0, rotim restul spre dreapta
                var last = wheel[n - 1];
                for (var k = n - 1; k > 1; k--) wheel[k] = wheel[k - 1];
                wheel[1] = last;
            }
        }
        return rounds;
    }

    /* ============================================================
       ROUND ROBIN — MESE DE N (board games), VARIETATE MAXIMĂ.
       Nu există „fiecare cu fiecare" pur la N>2; obiectivul este ca
       fiecare jucător să întâlnească pe cât mai mulți adversari
       DIFERIȚI de-a lungul rundelor, cu bye-uri echilibrate.

       Algoritm (greedy pe „datorie de întâlniri”):
       - ținem un contor cât de des s-au întâlnit oricare doi jucători;
       - la fiecare rundă construim mese de `seatsPerTable` alegând, pentru
         fiecare masă, jucători care s-au întâlnit cât mai PUȚIN între ei;
       - jucătorii care „stau pe tușă” într-o rundă (când nu se împart exact)
         primesc bye, echilibrat (cine a stat mai rar iese primul).
       - numărul de runde = `opts.rounds` (implicit: cât să acopere rezonabil,
         ~ ceil((n-1)/(seatsPerTable-1)), min 1).
       ============================================================ */
    function generateRoundRobinTables(playerIds, seatsPerTable, opts) {
        opts = opts || {};
        var ids = (playerIds || []).slice();
        var N = ids.length;
        var seats = Math.max(2, Math.min(12, seatsPerTable | 0 || 2));

        if (N < 2) return [];
        if (seats === 2) return generateRoundRobin1v1(ids, opts); // cazul 1v1 pur

        // Determinist opțional (seed) — util în teste.
        var rnd = (opts.seed != null) ? _mulberry32(opts.seed) : Math.random;

        // câte runde generăm
        var defaultRounds = Math.max(1, Math.ceil((N - 1) / (seats - 1)));
        var totalRounds = Math.max(1, (opts.rounds | 0) || defaultRounds);

        // contor de întâlniri: met[a][b] = de câte ori au fost la aceeași masă
        var met = {};
        ids.forEach(function (a) { met[a] = {}; ids.forEach(function (b) { if (a !== b) met[a][b] = 0; }); });
        var byeCount = {}; ids.forEach(function (a) { byeCount[a] = 0; });

        function pairCost(a, b) { return met[a] && met[a][b] != null ? met[a][b] : 0; }

        // costul de a adăuga `cand` la o masă parțial formată `tableArr`
        function joinCost(tableArr, cand) {
            var c = 0;
            for (var i = 0; i < tableArr.length; i++) c += pairCost(tableArr[i], cand);
            return c;
        }

        var rounds = [];

        for (var r = 0; r < totalRounds; r++) {
            var pool = _shuffle(ids, rnd); // amestec ca să spargem egalitățile echitabil

            // câți joacă efectiv runda asta (multiplu de `seats`); restul = bye
            var playing = Math.floor(pool.length / seats) * seats;
            var numByes = pool.length - playing;

            // alegem cine stă pe bye: cei cu byeCount minim ies primii
            var byeThisRound = [];
            if (numByes > 0) {
                var sortedForBye = pool.slice().sort(function (a, b) {
                    if (byeCount[a] !== byeCount[b]) return byeCount[a] - byeCount[b];
                    return rnd() - 0.5;
                });
                byeThisRound = sortedForBye.slice(0, numByes);
                byeThisRound.forEach(function (p) { byeCount[p]++; });
            }
            var byeSet = {}; byeThisRound.forEach(function (p) { byeSet[p] = true; });
            var active = pool.filter(function (p) { return !byeSet[p]; });

            // construim mese greedy: fiecare masă începe cu jucătorul „cel mai dator”
            // și adaugă pe rând jucătorul cu joinCost minim
            var remaining = active.slice();
            var tables = [];
            var tableNo = 0;

            while (remaining.length >= seats) {
                tableNo++;
                // seed masă: jucătorul cu cele mai puține întâlniri totale (cel mai „proaspăt”)
                remaining.sort(function (a, b) {
                    var sa = 0, sb = 0;
                    for (var k in met[a]) sa += met[a][k];
                    for (var k2 in met[b]) sb += met[b][k2];
                    if (sa !== sb) return sa - sb;
                    return rnd() - 0.5;
                });
                var tbl = [remaining.shift()];
                while (tbl.length < seats) {
                    // alegem candidatul cu joinCost minim față de masa curentă
                    var bestIdx = 0, bestCost = Infinity;
                    for (var i = 0; i < remaining.length; i++) {
                        var jc = joinCost(tbl, remaining[i]);
                        if (jc < bestCost) { bestCost = jc; bestIdx = i; }
                    }
                    tbl.push(remaining.splice(bestIdx, 1)[0]);
                }
                // actualizăm contorul de întâlniri pentru masa formată
                for (var x = 0; x < tbl.length; x++) {
                    for (var y = x + 1; y < tbl.length; y++) {
                        met[tbl[x]][tbl[y]]++; met[tbl[y]][tbl[x]]++;
                    }
                }
                var seatArr = tbl.map(function (pid, idx) {
                    return { seat_no: idx + 1, profile_id: pid, is_bye: false };
                });
                tables.push({ table_no: tableNo, seats: seatArr });
            }

            rounds.push({ round_no: r + 1, label: null, tables: tables });
        }

        return rounds;
    }

    /* ============================================================
       VALIDATOR de integritate a unui pairing (garanția pe care am
       promis-o server-side: NICIUN jucător de două ori în ACEEAȘI rundă).
       Întoarce { ok:true } sau { ok:false, error:'...' , detail:{...} }.
       E apelat de motor înainte de a trimite la RPC (Batch 3) și de self-test.
       ============================================================ */
    function validatePairing(rounds, participantIds) {
        try {
            if (!Array.isArray(rounds)) return { ok: false, error: 'NOT_ARRAY' };
            var partSet = {};
            (participantIds || []).forEach(function (id) { partSet[String(id)] = true; });

            for (var r = 0; r < rounds.length; r++) {
                var round = rounds[r];
                var seenThisRound = {};
                var tables = round.tables || [];
                for (var t = 0; t < tables.length; t++) {
                    var seats = tables[t].seats || [];
                    for (var s = 0; s < seats.length; s++) {
                        var pid = seats[s].profile_id;
                        if (pid == null) continue; // bye
                        var key = String(pid);
                        // 1) niciun jucător de două ori în aceeași rundă
                        if (seenThisRound[key]) {
                            return { ok: false, error: 'DUP_IN_ROUND', detail: { round: round.round_no, profile_id: pid } };
                        }
                        seenThisRound[key] = true;
                        // 2) doar participanți cunoscuți (dacă ni s-a dat lista)
                        if (participantIds && !partSet[key]) {
                            return { ok: false, error: 'UNKNOWN_PLAYER', detail: { round: round.round_no, profile_id: pid } };
                        }
                    }
                }
            }
            return { ok: true };
        } catch (e) {
            _tlog('validatePairing', e);
            return { ok: false, error: 'EXCEPTION' };
        }
    }

    /* ============================================================
       SELF-TEST (rulează manual din consolă: TO_TOURN._selfTest())
       Nu rulează automat la încărcare. Întoarce un array de rezultate
       PASS/FAIL + le loghează. Pur, nu atinge DB.
       ============================================================ */
    function _selfTest() {
        var results = [];
        function check(name, cond, got) {
            results.push({ test: name, pass: !!cond, got: got });
        }

        // helper: numără câte perechi distincte s-au întâlnit
        function distinctPairsMet(rounds) {
            var seen = {};
            rounds.forEach(function (rd) {
                (rd.tables || []).forEach(function (tb) {
                    var ps = (tb.seats || []).map(function (s) { return s.profile_id; }).filter(function (x) { return x != null; });
                    for (var i = 0; i < ps.length; i++)
                        for (var j = i + 1; j < ps.length; j++) {
                            var k = [ps[i], ps[j]].sort().join('|');
                            seen[k] = (seen[k] || 0) + 1;
                        }
                });
            });
            return seen;
        }

        // 1) RR 1v1 cu 4 jucători (par): 3 runde, fiecare pereche exact o dată
        var p4 = ['a', 'b', 'c', 'd'];
        var rr4 = generateRoundRobin1v1(p4, { legs: 1 });
        check('RR1v1(4) => 3 runde', rr4.length === 3, rr4.length);
        var pairs4 = distinctPairsMet(rr4);
        check('RR1v1(4) => 6 perechi distincte', Object.keys(pairs4).length === 6, Object.keys(pairs4).length);
        check('RR1v1(4) => fiecare pereche exact 1x', Object.keys(pairs4).every(function (k) { return pairs4[k] === 1; }), pairs4);
        check('RR1v1(4) integritate', validatePairing(rr4, p4).ok, validatePairing(rr4, p4));

        // 2) RR 1v1 cu 5 jucători (impar): 5 runde, fiecare are exact un bye
        var p5 = ['a', 'b', 'c', 'd', 'e'];
        var rr5 = generateRoundRobin1v1(p5, { legs: 1 });
        check('RR1v1(5) => 5 runde', rr5.length === 5, rr5.length);
        var byesOk = rr5.every(function (rd) {
            var byes = 0;
            (rd.tables || []).forEach(function (tb) {
                (tb.seats || []).forEach(function (s) { if (s.is_bye) byes++; });
            });
            return byes === 1;
        });
        check('RR1v1(5) => exact 1 bye/rundă', byesOk, byesOk);
        check('RR1v1(5) integritate', validatePairing(rr5, p5).ok, true);

        // 3) RR 1v1 tur-retur (legs=2) cu 4 jucători: 6 runde, fiecare pereche de 2x
        var rr4d = generateRoundRobin1v1(p4, { legs: 2 });
        check('RR1v1(4,legs2) => 6 runde', rr4d.length === 6, rr4d.length);
        var pairs4d = distinctPairsMet(rr4d);
        check('RR1v1(4,legs2) => fiecare pereche 2x', Object.keys(pairs4d).every(function (k) { return pairs4d[k] === 2; }), pairs4d);

        // 4) Mese de N: 8 jucători, mese de 4, determinist (seed)
        var p8 = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8'];
        var tN = generateRoundRobinTables(p8, 4, { rounds: 3, seed: 42 });
        check('Tables(8,seats4,3r) => 3 runde', tN.length === 3, tN.length);
        var allTables4 = tN.every(function (rd) { return (rd.tables || []).every(function (tb) { return tb.seats.length === 4; }); });
        check('Tables(8,seats4) => toate mesele au 4 locuri', allTables4, allTables4);
        check('Tables(8,seats4) integritate (fără dublură/rundă)', validatePairing(tN, p8).ok, validatePairing(tN, p8));

        // 5) Mese de N cu bye: 10 jucători, mese de 4 => 2 mese (8) + 2 bye/rundă
        var p10 = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10'];
        var tBye = generateRoundRobinTables(p10, 4, { rounds: 3, seed: 7 });
        var seatCountOk = tBye.every(function (rd) {
            var seated = 0;
            (rd.tables || []).forEach(function (tb) { seated += tb.seats.length; });
            return seated === 8; // 2 mese pline; 2 pe bye
        });
        check('Tables(10,seats4) => 8 așezați/rundă (2 bye)', seatCountOk, seatCountOk);
        check('Tables(10,seats4) integritate', validatePairing(tBye, p10).ok, validatePairing(tBye, p10));

        // 6) Varietate: la mese de N, media întâlnirilor repetate să fie mică
        var pairsN = distinctPairsMet(tN);
        var maxRepeat = Math.max.apply(null, Object.keys(pairsN).map(function (k) { return pairsN[k]; }));
        check('Tables(8,seats4) => varietate (max repetări pereche <= 2)', maxRepeat <= 2, maxRepeat);

        var passed = results.filter(function (r) { return r.pass; }).length;
        var total = results.length;
        try {
            console.log('%c[tournaments] self-test: ' + passed + '/' + total + ' PASS',
                'color:' + (passed === total ? '#2ecc71' : '#e74c3c') + ';font-weight:bold');
            results.forEach(function (r) {
                console.log((r.pass ? '  ✓ ' : '  ✗ ') + r.test, r.pass ? '' : ('=> got:'), r.pass ? '' : r.got);
            });
        } catch (e) {}
        return { passed: passed, total: total, results: results };
    }

    /* ============================================================
       INFRASTRUCTURĂ CLIENT — capabilități, caller-e RPC, loadere.
       Toate se sprijină pe infrastructura existentă din supabase-integration.js
       (sb, currentClubId, requireFeature, showNotification, t) — pe care le
       accesăm ca globale (scope partajat). NU modificăm supabase-integration.js.
       ============================================================ */

    /* ---- 1. Înregistrare capabilități (fără a atinge supabase-integration.js) ----
       Mutăm intrări noi în CAP_MIN_RANK (obiect global const, mutabil).
       tournament.manage : 2 (Moderator+) — creare/editare/status/bracket/rezultate
       tournament.join   : 1 (Membru+)    — înscriere self-service                  */
    function _registerCaps() {
        try {
            if (typeof CAP_MIN_RANK === 'object' && CAP_MIN_RANK) {
                if (CAP_MIN_RANK['tournament.manage'] == null) CAP_MIN_RANK['tournament.manage'] = 2;
                if (CAP_MIN_RANK['tournament.join'] == null) CAP_MIN_RANK['tournament.join'] = 1;
            }
        } catch (e) { _tlog('_registerCaps', e); }
    }

    /* ---- 2. Mapare erori RPC -> mesaje i18n RO+EN ---- */
    var _KNOWN_CODES = [
        'NOT_AUTHENTICATED', 'FORBIDDEN', 'PLAN_REQUIRED', 'CLUB_NOT_WRITABLE', 'CLUB_NOT_FOUND',
        'NAME_REQUIRED', 'BAD_FORMAT', 'BAD_GAME', 'LOCKED_AFTER_START', 'BAD_STATUS', 'BAD_TRANSITION',
        'GAME_REQUIRED', 'BAD_ADVANCE_CONFIG', 'NOT_ENOUGH_PARTICIPANTS', 'TOURNAMENT_NOT_FOUND',
        'NOT_MEMBER', 'BANNED', 'REGISTRATION_CLOSED', 'DEADLINE_PASSED', 'TOURNAMENT_FULL',
        'NOT_REGISTERED', 'BAD_STATE_FOR_PAIRING', 'EMPTY_PAIRING', 'NO_ACTIVE_PARTICIPANTS',
        'PLAYER_NOT_PARTICIPANT', 'BAD_ROUND_NO', 'BAD_TABLE_NO', 'BAD_SEAT_NO', 'BAD_TABLES', 'BAD_SEATS',
        'TABLE_NOT_FOUND', 'TOURNAMENT_NOT_LIVE', 'EMPTY_RESULTS', 'NO_PLAYERS_AT_TABLE', 'BAD_SEAT_RESULT', 'PLAYER_NOT_AT_TABLE',
        'WINNER_NOT_PARTICIPANT', 'FINAL_POINTS_NEEDS_ADMIN', 'FINAL_POINTS_BAD_TARGET', 'BAD_STATUS_TRANSITION',
        'ROUND_NOT_COMPLETE', 'NO_ADVANCERS', 'BAD_FORMAT_FOR_ADVANCE', 'NO_ROUNDS', 'ONLY_DRAFT_DELETABLE'
    ];
    /* Extrage codul cunoscut dintr-un obiect de eroare Supabase / string. Pur. */
    function _errCode(err) {
        if (err == null) return '';
        var msg = (typeof err === 'string') ? err
            : String(err.message || err.error_description || err.details || err.hint || '');
        msg = msg.trim();
        for (var i = 0; i < _KNOWN_CODES.length; i++) {
            if (msg === _KNOWN_CODES[i] || msg.indexOf(_KNOWN_CODES[i]) === 0) return _KNOWN_CODES[i];
        }
        return '';
    }
    /* Mesaj tradus pentru o eroare (fallback la generic). */
    function _tErrMsg(err) {
        var tf = (typeof window.t === 'function') ? window.t : function (k, f) { return f || k; };
        var code = _errCode(err);
        if (code) return tf('tourn.err.' + code, code);
        return tf('tourn.err.generic', 'Something went wrong. Please try again.');
    }
    /* Toast de eroare prietenos. */
    function _toToastErr(err) {
        try {
            if (typeof showNotification === 'function') showNotification(_tErrMsg(err), 'error');
            else _tlog('toErr', err);
        } catch (e) { _tlog('_toToastErr', e); }
    }
    /* Toast de succes scurt. */
    function _toToastOk(key, fallback) {
        try {
            var tf = (typeof window.t === 'function') ? window.t : function (k, f) { return f || k; };
            if (typeof showNotification === 'function') showNotification(tf(key, fallback), 'success');
        } catch (e) { _tlog('_toToastOk', e); }
    }

    /* ---- 3. Caller-e RPC (DB-first; local DOAR la succes; §23.2) ----
       Fiecare întoarce datele la succes sau null la eșec (după ce a arătat toast).
       Nu mută nicio stare locală — asta face UI-ul din batch-urile 3b/3c. */

    function _sbReady() {
        return (typeof sb !== 'undefined' && sb && typeof sb.rpc === 'function');
    }
    function _clubId() {
        return (typeof currentClubId !== 'undefined') ? currentClubId : null;
    }

    /* ============================================================
       NOTIFICĂRI DE TURNEU — folosesc pushNotification (circuitul app + push).
       Client-side, după acțiuni reușite; scrie în tabela `notifications`
       (merge cross-device). Actorul e exclus din destinatari.
       ============================================================ */
    function _toActorId() {
        return (typeof state !== 'undefined' && state.currentUser) ? state.currentUser.id : null;
    }

    /* id-urile membrilor activi ai clubului (fără actor). */
    async function _toActiveMemberIds(clubId) {
        try {
            var res = await sb.from('club_members').select('profile_id').eq('club_id', clubId).eq('status', 'active');
            if (res.error) { _tlog('_toActiveMemberIds', res.error); return []; }
            var me = _toActorId();
            return (res.data || []).map(function (m) { return m.profile_id; }).filter(function (id) { return id && id !== me; });
        } catch (e) { _tlog('_toActiveMemberIds', e); return []; }
    }

    /* participanții activi (registered/checked_in) dintr-un set de date turneu (fără actor). */
    function _toParticipantIds(data) {
        var me = _toActorId();
        return ((data && data.participants) || [])
            .filter(function (p) { return p.status === 'registered' || p.status === 'checked_in'; })
            .map(function (p) { return p.profile_id; })
            .filter(function (id) { return id && id !== me; });
    }

    /* trimite o notificare de turneu (dacă pushNotification e disponibil). */
    function _toNotify(ids, tournamentId, text, icon) {
        try {
            if (!ids || !ids.length || typeof pushNotification !== 'function') return;
            pushNotification(ids, {
                clubId: _clubId(),
                type: 'tournament',
                icon: icon || 'fa-trophy',
                tab: 'tournaments',
                text: text,
                data: { route: 'tournaments', tournamentId: tournamentId }
            });
        } catch (e) { _tlog('_toNotify', e); }
    }

    /* text i18n cu numele turneului interpolat pe {name}. */
    function _toNotifText(key, fallback, name) {
        var s = _tt(key, fallback);
        return s.replace('{name}', name || '');
    }

    async function toCreateTournament(payload) {
        if (typeof requireFeature === 'function' && !requireFeature('tournaments')) return null;
        if (!_sbReady()) { _tlog('toCreateTournament', 'sb indisponibil'); return null; }
        try {
            var res = await sb.rpc('tournament_create', { p_club_id: _clubId(), p_payload: payload || {} });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.created', 'Tournament created.');
            return res.data;
        } catch (e) { _tlog('toCreateTournament', e); _toToastErr(e); return null; }
    }

    async function toUpdateTournament(tournamentId, payload) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_update', { p_tournament_id: tournamentId, p_payload: payload || {} });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.updated', 'Tournament updated.');
            return res.data;
        } catch (e) { _tlog('toUpdateTournament', e); _toToastErr(e); return null; }
    }

    /* Șterge un turneu draft (creator sau Admin). */
    async function toDeleteTournament(tournamentId) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_delete', { p_id: tournamentId });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.deleted', 'Tournament deleted.');
            return res.data;
        } catch (e) { _tlog('toDeleteTournament', e); _toToastErr(e); return null; }
    }

    /* Pagina publică — toggle (creator sau Admin). */
    async function toSetPublic(tournamentId, enabled) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_set_public', { p_id: tournamentId, p_enabled: !!enabled });
            if (res.error) { _toToastErr(res.error); return null; }
            return res.data;
        } catch (e) { _tlog('toSetPublic', e); _toToastErr(e); return null; }
    }
    async function _toMakePublic(id) {
        var res = await toSetPublic(id, true);
        if (res) { _toToastOk('tourn.public.enabled', 'Public link created.'); await _renderTournamentPageBody(id); }
    }
    async function _toMakePrivate(id) {
        var ok = (typeof confirmModal === 'function')
            ? await confirmModal(_tt('tourn.public.confirmPrivate', 'Make this tournament private? The public link will stop working.'), { danger: true, title: _tt('tourn.public.makePrivate', 'Make private') })
            : true;
        if (!ok) return;
        var res = await toSetPublic(id, false);
        if (res) { _toToastOk('tourn.public.disabled', 'Public link revoked.'); await _renderTournamentPageBody(id); }
    }
    function _toCopyPublicLink() {
        var el = document.getElementById('toPublicLink');
        if (!el) return;
        try {
            el.select();
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(el.value);
            else document.execCommand('copy');
            if (typeof showNotification === 'function') showNotification(_tt('tourn.public.copied', 'Link copied.'), 'success');
        } catch (e) { _tlog('_toCopyPublicLink', e); }
    }

    async function toSetTournamentStatus(tournamentId, status) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_set_status', { p_tournament_id: tournamentId, p_status: status });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.statusChanged', 'Tournament status changed.');
            return res.data;
        } catch (e) { _tlog('toSetTournamentStatus', e); _toToastErr(e); return null; }
    }

    async function toJoinTournament(tournamentId) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_join', { p_tournament_id: tournamentId });
            if (res.error) { _toToastErr(res.error); return null; }
            // feedback diferit: waitlist vs înscris
            var st = res.data && res.data.status;
            if (st === 'waitlisted') _toToastOk('tourn.waitlisted', "You're on the waitlist");
            else _toToastOk('tourn.joined', 'Successfully registered!');
            return res.data;
        } catch (e) { _tlog('toJoinTournament', e); _toToastErr(e); return null; }
    }

    async function toLeaveTournament(tournamentId) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_leave', { p_tournament_id: tournamentId });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.left', 'You withdrew from the tournament.');
            return res.data; // { left:true, promoted:<id|null> } — UI notifică promovatul (3c)
        } catch (e) { _tlog('toLeaveTournament', e); _toToastErr(e); return null; }
    }

    /* Aplică un pairing pre-calculat: validează LOCAL (garanția anti-dublură),
       apoi trimite la RPC-ul atomic. participantIds e opțional (validare extra). */
    async function toApplyPairing(tournamentId, rounds, participantIds) {
        var v = validatePairing(rounds, participantIds);
        if (!v.ok) {
            _tlog('toApplyPairing.validate', v);
            try {
                var tf = (typeof window.t === 'function') ? window.t : function (k, f) { return f || k; };
                if (typeof showNotification === 'function')
                    showNotification(tf('tourn.err.pairingInvalid', 'Invalid pairing.'), 'error');
            } catch (e) {}
            return null;
        }
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_apply_pairing', { p_tournament_id: tournamentId, p_rounds: rounds });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.bracketGenerated', 'Bracket generated.');
            return res.data;
        } catch (e) { _tlog('toApplyPairing', e); _toToastErr(e); return null; }
    }

    /* Raportează rezultatul unei mese (Moderator+). results = {seats:[{profile_id,placement,score?}], date?}. */
    async function toReportTable(tableId, results) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_report_table', { p_table_id: tableId, p_results: results || {} });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.reported', 'Result saved.');
            return res.data;
        } catch (e) { _tlog('toReportTable', e); _toToastErr(e); return null; }
    }

    /* Finalizează turneul (Moderator+). winnerId poate fi null; finalPoints (bonus) doar Admin. */
    async function toFinalize(id, winnerId, finalPoints) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_finalize', { p_id: id, p_winner_id: winnerId || null, p_final_points: finalPoints || null });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.finalized', 'Tournament finalized.');
            return res.data;
        } catch (e) { _tlog('toFinalize', e); _toToastErr(e); return null; }
    }

    /* Redeschide un turneu finalizat (Admin only). */
    async function toReopen(id) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_reopen', { p_id: id });
            if (res.error) { _toToastErr(res.error); return null; }
            _toToastOk('tourn.reopened', 'Tournament reopened.');
            return res.data;
        } catch (e) { _tlog('toReopen', e); _toToastErr(e); return null; }
    }

    /* Avansează în runda următoare la single-elim (Moderator+, atomic). */
    async function toAdvanceElim(id) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_advance_elim', { p_id: id });
            if (res.error) { _toToastErr(res.error); return null; }
            return res.data;
        } catch (e) { _tlog('toAdvanceElim', e); _toToastErr(e); return null; }
    }

    /* Starea Swiss (verificare server-side): runde jucate/configurate + rundă completă. */
    async function toSwissState(id) {
        if (!_sbReady()) return null;
        try {
            var res = await sb.rpc('tournament_swiss_state', { p_id: id });
            if (res.error) { _toToastErr(res.error); return null; }
            return res.data;
        } catch (e) { _tlog('toSwissState', e); _toToastErr(e); return null; }
    }

    /* Clasamentul de serie anuală (read-only). */
    async function toSeriesStandings(year) {
        if (!_sbReady()) return null;
        var cid = _clubId();
        if (!cid) return null;
        try {
            var res = await sb.rpc('tournament_series_standings', { p_club_id: cid, p_year: year });
            if (res.error) { _toToastErr(res.error); return null; }
            return res.data;
        } catch (e) { _tlog('toSeriesStandings', e); _toToastErr(e); return null; }
    }

    /* ---- Scoring: replic exact logica clubului (perRank + defaultMode) ---- */
    /* config efectiv al șabloanelor (din state, cu fallback defensiv identic clientului). */
    function _scoringCfg() {
        var s = (typeof state !== 'undefined' && state.scoringTemplates
                 && typeof state.scoringTemplates === 'object' && !Array.isArray(state.scoringTemplates))
                ? state.scoringTemplates : {};
        var perRank = (s.competitive && Array.isArray(s.competitive.perRank) && s.competitive.perRank.length)
            ? s.competitive.perRank.map(Number).filter(function (n) { return !isNaN(n); })
            : [2, 1, 0.5, 0];
        return {
            enabled: (s.enabled === true || s.enabled === 'true'),
            defaultMode: (s.defaultMode === 'uniform') ? 'uniform' : 'raw',
            perRank: perRank.length ? perRank : [0]
        };
    }
    /* modul implicit de scoring al clubului (uniform doar dacă activat + ales). */
    function _clubDefaultMode() {
        var c = _scoringCfg();
        return (c.enabled && c.defaultMode === 'uniform') ? 'uniform' : 'raw';
    }
    /* puncte pentru un loc (1-based); ultima valoare din perRank se aplică tuturor locurilor inferioare. */
    function _ptsForPlace(place, perRank) {
        var idx = Math.min(Math.max(place, 1) - 1, perRank.length - 1);
        var v = Number(perRank[idx]);
        return isNaN(v) ? 0 : v;
    }
    /* construiește club_points {pid: puncte} din plasamente, folosind perRank. */
    function _clubPointsFromPlacements(seats) {
        var c = _scoringCfg();
        var out = {};
        seats.forEach(function (s) { out[s.profile_id] = _ptsForPlace(s.placement, c.perRank); });
        return out;
    }

    /* ============================================================
       SINGLE-ELIM cu mese de N (avansează primii X de la fiecare masă).
       Format dependent de rezultate => generăm O SINGURĂ rundă odată, din
       lista jucătorilor care avansează. Aceeași structură de output ca RR:
       { round_no, label, tables:[{ table_no, seats:[{seat_no,profile_id,is_bye}] }] }
       ============================================================ */
    function generateElimRound(playerIds, seatsPerTable, roundNo, opts) {
        opts = opts || {};
        var ids = (playerIds || []).filter(Boolean).slice();
        var seats = Math.max(2, Math.min(12, seatsPerTable | 0 || 2));
        if (ids.length < 2) return null;               // campion deja

        var tables = [];
        var tableNo = 1;
        for (var i = 0; i < ids.length; i += seats) {
            var chunk = ids.slice(i, i + seats);
            var seatArr = chunk.map(function (pid, idx) {
                return { seat_no: idx + 1, profile_id: pid, is_bye: false };
            });
            for (var s = chunk.length; s < seats; s++) {
                seatArr.push({ seat_no: s + 1, profile_id: null, is_bye: true });
            }
            tables.push({ table_no: tableNo++, seats: seatArr });
        }
        return { round_no: roundNo, label: null, tables: tables };
    }

    /* Câți avansează de la o masă (top_x_advance), cu minim 1 și maxim seats-1. */
    function _advancePerTable(topX, seatsPerTable) {
        var seats = Math.max(2, seatsPerTable | 0 || 2);
        var x = (topX | 0) || 1;
        return Math.max(1, Math.min(seats - 1, x));
    }

    /* self-test motor single-elim (rulează la load, ca RR). */
    function _selfTestElim() {
        var passed = 0, total = 0, fails = [];
        function ok(name, cond) { total++; if (cond) passed++; else fails.push(name); }

        var r1 = generateElimRound(['a','b','c','d','e','f','g','h'], 4, 1, { seed: 1 });
        ok('8/4 -> 2 mese', r1 && r1.tables.length === 2);
        ok('8/4 -> mese pline', r1.tables.every(function (t) { return t.seats.filter(function (s){return !s.is_bye;}).length === 4; }));

        var r2 = generateElimRound(['a','b','c','d','e','f'], 4, 1, { seed: 1 });
        ok('6/4 -> 2 mese', r2 && r2.tables.length === 2);
        ok('6/4 -> masa2 are 2 bye', r2.tables[1].seats.filter(function (s){return s.is_bye;}).length === 2);
        ok('6/4 -> total 6 reali', r2.tables.reduce(function (a,t){return a+t.seats.filter(function(s){return !s.is_bye;}).length;},0) === 6);

        var r3 = generateElimRound(['a','b','c','d'], 4, 3, { seed: 1 });
        ok('4/4 -> 1 masă (finala)', r3 && r3.tables.length === 1);

        ok('1 -> campion (null)', generateElimRound(['a'], 4, 5) === null);

        ok('advance top2/4', _advancePerTable(2, 4) === 2);
        ok('advance clamp la seats-1', _advancePerTable(9, 4) === 3);
        ok('advance minim 1', _advancePerTable(0, 4) === 1);

        return { passed: passed, total: total, fails: fails };
    }

    /* ============================================================
       SWISS cu mese de N (best-effort evitare reîntâlniri).
       Toți joacă un număr fix de runde; în fiecare rundă, grupare la mese
       după clasamentul curent, evitând pe cât posibil colegii de masă deja
       întâlniți. Dependent de rezultate => o rundă odată.

       generateSwissRound(rankedIds, seatsPerTable, roundNo, pastPairs, opts):
         - rankedIds: id-uri ORDONATE după clasamentul curent (cel mai bun primul);
         - pastPairs: set de perechi deja întâlnite, ca "a|b" (a<b lexicografic);
         - grupare greedy: pornim de la cel mai bun neașezat, adăugăm la masă
           următorii din clasament care nu l-au întâlnit (dacă se poate), altfel
           relaxăm constrângerea (best-effort);
         - ultima masă se completează cu bye la nevoie.
       Întoarce { round_no, tables:[...] } sau null dacă < 2 jucători.
       ============================================================ */
    function generateSwissRound(rankedIds, seatsPerTable, roundNo, pastPairs, opts) {
        opts = opts || {};
        var ids = (rankedIds || []).filter(Boolean).slice();
        var seats = Math.max(2, Math.min(12, seatsPerTable | 0 || 2));
        if (ids.length < 2) return null;
        var past = pastPairs || {};

        function pairKey(a, b) { return (String(a) < String(b)) ? (a + '|' + b) : (b + '|' + a); }
        function metBefore(a, b) { return !!past[pairKey(a, b)]; }

        var remaining = ids.slice();      // în ordinea clasamentului
        var tables = [];
        var tableNo = 1;

        while (remaining.length > 0) {
            var seed = remaining.shift();        // cel mai bine clasat neașezat
            var group = [seed];

            // completăm masa: preferăm jucători care NU l-au întâlnit pe niciunul din grup
            // (best-effort). Facem două treceri: întâi strict, apoi relaxat.
            function tryFill(strict) {
                for (var i = 0; i < remaining.length && group.length < seats; i++) {
                    var cand = remaining[i];
                    if (cand == null) continue;
                    if (strict) {
                        var conflict = false;
                        for (var g = 0; g < group.length; g++) {
                            if (metBefore(group[g], cand)) { conflict = true; break; }
                        }
                        if (conflict) continue;
                    }
                    group.push(cand);
                    remaining[i] = null;
                }
                // compactăm remaining (scoatem null-urile)
                remaining = remaining.filter(function (x) { return x != null; });
            }

            tryFill(true);                        // întâi fără reîntâlniri
            if (group.length < seats) tryFill(false);  // relaxat, dacă n-am umplut

            var seatArr = group.map(function (pid, idx) {
                return { seat_no: idx + 1, profile_id: pid, is_bye: false };
            });
            for (var s = group.length; s < seats; s++) {
                seatArr.push({ seat_no: s + 1, profile_id: null, is_bye: true });
            }
            tables.push({ table_no: tableNo++, seats: seatArr });
        }
        return { round_no: roundNo, label: null, tables: tables };
    }

    /* Nr. sugerat de runde Swiss: cât să diferențieze clasamentul rezonabil.
       ~ ceil(log(N)/log(seats)) cu minim 3, plafonat la N-1. */
    function _suggestSwissRounds(nPlayers, seatsPerTable) {
        var n = Math.max(2, nPlayers | 0);
        var seats = Math.max(2, seatsPerTable | 0 || 2);
        var r = Math.ceil(Math.log(n) / Math.log(seats));
        return Math.max(3, Math.min(n - 1, r + 1));
    }

    /* extrage perechile de coechipieri dintr-o rundă (pt. istoricul de reîntâlniri). */
    function _pairsFromRound(tables) {
        var out = {};
        (tables || []).forEach(function (tb) {
            var pids = (tb.seats || []).filter(function (s) { return s.profile_id != null && !s.is_bye; })
                .map(function (s) { return s.profile_id; });
            for (var i = 0; i < pids.length; i++) {
                for (var j = i + 1; j < pids.length; j++) {
                    var a = pids[i], b = pids[j];
                    out[(String(a) < String(b)) ? (a + '|' + b) : (b + '|' + a)] = true;
                }
            }
        });
        return out;
    }

    /* self-test motor Swiss. */
    function _selfTestSwiss() {
        var passed = 0, total = 0, fails = [];
        function ok(name, cond) { total++; if (cond) passed++; else fails.push(name); }

        // 8 jucători, mese de 4 => 2 mese pline
        var r1 = generateSwissRound(['a','b','c','d','e','f','g','h'], 4, 1, {});
        ok('8/4 -> 2 mese', r1 && r1.tables.length === 2);
        ok('8/4 -> mese pline', r1.tables.every(function (t) { return t.seats.filter(function (s){return !s.is_bye;}).length === 4; }));

        // integritate: fiecare jucător exact o dată
        var seen = {};
        r1.tables.forEach(function (t) { t.seats.forEach(function (s) { if (s.profile_id) seen[s.profile_id] = (seen[s.profile_id]||0)+1; }); });
        ok('8/4 -> fiecare o dată', Object.keys(seen).length === 8 && Object.keys(seen).every(function (k){return seen[k]===1;}));

        // evitare reîntâlniri: cazul EVITABIL (mese de 2). R1: a-b,c-d,e-f,g-h.
        var s1 = generateSwissRound(['a','b','c','d','e','f','g','h'], 2, 1, {});
        var pastS = _pairsFromRound(s1.tables);
        ok('past pairs runda1 (a-b)', pastS['a|b'] === true);
        // R2 după clasament: best-effort trebuie să evite COMPLET reîntâlnirile
        var s2 = generateSwissRound(['a','c','e','g','b','d','f','h'], 2, 2, pastS);
        var s2pairs = _pairsFromRound(s2.tables);
        var reunions = Object.keys(s2pairs).filter(function (k) { return pastS[k]; }).length;
        ok('mese de 2: runda2 evită COMPLET reîntâlnirile', reunions === 0);

        // 6 jucători, mese de 4 => masa1 plină, masa2 cu 2 reali + 2 bye
        var r3 = generateSwissRound(['a','b','c','d','e','f'], 4, 1, {});
        ok('6/4 -> 2 mese', r3.tables.length === 2);
        ok('6/4 -> total 6 reali', r3.tables.reduce(function(a,t){return a+t.seats.filter(function(s){return !s.is_bye;}).length;},0) === 6);

        // < 2 => null
        ok('1 -> null', generateSwissRound(['a'], 4, 1, {}) === null);

        // sugestie runde
        ok('suggest rounds >=3', _suggestSwissRounds(8, 4) >= 3);
        ok('suggest rounds <= N-1', _suggestSwissRounds(4, 2) <= 3);

        return { passed: passed, total: total, fails: fails };
    }

    /* ---- 4. Loadere (citiri RLS: SELECT = membru al clubului) ---- */

    /* Lista turneelor clubului activ (cele mai noi primele). */
    async function toLoadTournaments(clubId) {
        var cid = clubId || _clubId();
        if (!_sbReady() || !cid) return [];
        try {
            var res = await sb.from('tournaments').select('*')
                .eq('club_id', cid).order('created_at', { ascending: false });
            if (res.error) { _tlog('toLoadTournaments', res.error); return []; }
            return res.data || [];
        } catch (e) { _tlog('toLoadTournaments', e); return []; }
    }

    /* Un turneu complet: antet + participanți + runde + mese + locuri (nested).
       Toate cheiate pe id de profil; UI-ul rezolvă id->nume la afișare. */
    async function toLoadTournament(id) {
        if (!_sbReady() || !id) return null;
        try {
            var out = await Promise.all([
                sb.from('tournaments').select('*').eq('id', id).maybeSingle(),
                sb.from('tournament_participants').select('*').eq('tournament_id', id).order('joined_at', { ascending: true }),
                sb.from('tournament_rounds').select('*').eq('tournament_id', id).order('round_no', { ascending: true }),
                sb.from('tournament_tables').select('*').eq('tournament_id', id).order('table_no', { ascending: true }),
                sb.from('tournament_seats').select('*').eq('tournament_id', id).order('seat_no', { ascending: true })
            ]);
            var tRes = out[0], pRes = out[1], rRes = out[2], tblRes = out[3], sRes = out[4];
            if (tRes.error || !tRes.data) { _tlog('toLoadTournament', tRes.error || 'not found'); return null; }

            var tournament = tRes.data;
            var participants = (pRes.data || []);
            var rounds = (rRes.data || []);
            var tables = (tblRes.data || []);
            var seats = (sRes.data || []);

            // asamblare nested: round -> tables -> seats
            var seatsByTable = {};
            seats.forEach(function (s) { (seatsByTable[s.table_id] = seatsByTable[s.table_id] || []).push(s); });
            var tablesByRound = {};
            tables.forEach(function (tb) {
                tb.seats = (seatsByTable[tb.id] || []).sort(function (a, b) { return a.seat_no - b.seat_no; });
                (tablesByRound[tb.round_id] = tablesByRound[tb.round_id] || []).push(tb);
            });
            rounds.forEach(function (rd) {
                rd.tables = (tablesByRound[rd.id] || []).sort(function (a, b) { return a.table_no - b.table_no; });
            });

            return {
                tournament: tournament,
                participants: participants,
                rounds: rounds
            };
        } catch (e) { _tlog('toLoadTournament', e); return null; }
    }

    /* ---- 5. Helpere de afișare (i18n pentru format/status) ---- */
    function toFormatLabel(fmt) {
        var tf = (typeof window.t === 'function') ? window.t : function (k, f) { return f || k; };
        return tf('tourn.format.' + fmt, fmt);
    }
    function toStatusLabel(st) {
        var tf = (typeof window.t === 'function') ? window.t : function (k, f) { return f || k; };
        return tf('tourn.status.' + st, st);
    }

    /* Rulează înregistrarea capabilităților acum (fișierul e încărcat după supabase). */
    _registerCaps();

    /* ============================================================
       UI — LISTA DE TURNEE (Batch 3b)
       renderTournaments(): încarcă turneele clubului activ și randează
       carduri. Apelată din switchTab (case 'tournaments') + realtime.
       Escape la ORICE câmp derivat din user (§9.1). Nume pe id (§23.1).
       ============================================================ */

    /* escape defensiv: refolosește escHtml global dacă există. */
    function _esc(s) {
        try { if (typeof window.escHtml === 'function') return window.escHtml(s); } catch (e) {}
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
        });
    }
    /* attrJs defensiv pentru id-uri în onclick. */
    function _attr(s) {
        try { if (typeof window.attrJs === 'function') return window.attrJs(s); } catch (e) {}
        return String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[&<>"]/g, function (c) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
        });
    }
    function _tt(key, fallback) {
        return (typeof window.t === 'function') ? window.t(key, fallback) : (fallback || key);
    }

    /* clasă de badge de status (culori discrete, reutilizează paleta existentă). */
    function _statusBadgeStyle(st) {
        switch (st) {
            case 'registration': return 'background:rgba(46,204,113,0.15); color:#2ecc71;';
            case 'live':         return 'background:rgba(255,107,53,0.15); color:#ff6b35;';
            case 'completed':    return 'background:rgba(155,89,182,0.15); color:#9b59b6;';
            case 'archived':     return 'background:rgba(127,140,141,0.15); color:#95a5a6;';
            default:             return 'background:rgba(127,140,141,0.12); color:#bbb;'; // draft
        }
    }

    /* un card de turneu în listă. */
    function _tournamentCardHtml(t) {
        var name = _esc(t.name);
        var game = _esc(t.game_title || '');
        var fmt = _esc(toFormatLabel(t.format));
        var st = t.status;
        var stLabel = _esc(toStatusLabel(st));
        var stStyle = _statusBadgeStyle(st);
        var deadline = '';
        if (t.registration_deadline) {
            try { deadline = new Date(t.registration_deadline).toLocaleDateString(); } catch (e) {}
        }
        var meta = [];
        if (game) meta.push('<i class="fas fa-dice"></i> ' + game);
        meta.push('<i class="fas fa-sitemap"></i> ' + fmt);
        if (deadline) meta.push('<i class="fas fa-hourglass-half"></i> ' + _esc(_tt('tourn.deadline', 'Registration deadline')) + ': ' + _esc(deadline));

        // buton edit/delete DOAR pe draft ȘI doar pentru creator sau Admin
        var actions = '';
        var me = (typeof state !== 'undefined' && state.currentUser) ? state.currentUser.id : null;
        var canManage = (st === 'draft') && (me && String(t.created_by) === String(me) || _toIsAdmin());
        if (canManage) {
            actions = '<div style="display:flex; gap:8px; margin-top:10px;" onclick="event.stopPropagation();">' +
                '<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation(); _toEditFromCard(\'' + _attr(t.id) + '\')"><i class="fas fa-pen"></i> ' + _esc(_tt('common.edit', 'Edit')) + '</button>' +
                '<button class="btn btn-sm btn-danger" onclick="event.stopPropagation(); _toDeleteFromCard(\'' + _attr(t.id) + '\')"><i class="fas fa-trash"></i> ' + _esc(_tt('common.delete', 'Delete')) + '</button>' +
                '</div>';
        }

        return '' +
            '<div class="card" style="margin-bottom:12px; cursor:pointer;" onclick="openTournamentPage(\'' + _attr(t.id) + '\')">' +
              '<div style="display:flex; justify-content:space-between; align-items:flex-start; gap:10px; flex-wrap:wrap;">' +
                '<div style="min-width:0;">' +
                  '<div style="font-weight:700; font-size:1.05em; overflow-wrap:anywhere;"><i class="fas fa-trophy" style="color:var(--accent-gold,#f1c40f);"></i> ' + name + '</div>' +
                  '<div style="font-size:0.82em; color:var(--text-secondary,#bbb); margin-top:6px; display:flex; gap:14px; flex-wrap:wrap;">' + meta.join(' ') + '</div>' +
                '</div>' +
                '<span style="flex:0 0 auto; padding:3px 10px; border-radius:12px; font-size:0.75em; font-weight:700; ' + stStyle + '">' + stLabel + '</span>' +
              '</div>' +
              actions +
            '</div>';
    }

    /* deschide editorul precompletat pentru un turneu (din card). */
    async function _toEditFromCard(id) {
        var t = null;
        for (var i = 0; i < (_tournListCache || []).length; i++) { if (String(_tournListCache[i].id) === String(id)) { t = _tournListCache[i]; break; } }
        if (!t) { var data = await toLoadTournament(id); t = data && data.tournament; }
        if (t) openTournamentEditor(t);
    }

    /* șterge un turneu draft (din card), cu confirmare. */
    async function _toDeleteFromCard(id) {
        var ok = (typeof confirmModal === 'function')
            ? await confirmModal(_tt('tourn.confirmDelete', 'Delete this tournament? This cannot be undone.'), { danger: true, title: _tt('common.delete', 'Delete') })
            : true;
        if (!ok) return;
        var res = await toDeleteTournament(id);
        if (res && typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
    }

    var _tournListCache = [];
    var _toSearchQuery = '';
    var _toSearchOpen = false;

    async function renderTournaments() {
        if (!_tournChannel) { try { subscribeRealtimeTournaments(); } catch (e) {} }
        var host = document.getElementById('tournamentsList');
        if (!host) return;
        _toMountHeaderTools();   // butoane search/export lângă „New" (o singură dată)
        host.innerHTML = '<div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' +
            _esc(_tt('tourn.loading', 'Loading tournaments…')) + '</div>';
        try {
            var list = await toLoadTournaments();
            _tournListCache = list || [];
            _toPaintList();
        } catch (e) {
            _tlog('renderTournaments', e);
            host.innerHTML = '<div style="padding:20px; text-align:center; color:var(--danger,#e74c3c);">' +
                _esc(_tt('tourn.err.generic', 'Something went wrong.')) + '</div>';
        }
    }

    /* desenare din cache, filtrată după căutare (fără refetch). */
    function _toPaintList() {
        var host = document.getElementById('tournamentsList');
        if (!host) return;
        var seriesBtn = '<div style="margin-bottom:12px;"><button class="btn btn-sm" onclick="openSeriesStandings()"><i class="fas fa-medal"></i> ' +
            _esc(_tt('tourn.series.title', 'Club champion (annual)')) + '</button></div>';

        if (!_tournListCache.length) {
            host.innerHTML = seriesBtn +
                '<div style="padding:24px; text-align:center; color:var(--text-secondary,#bbb);">' +
                '<div style="font-size:2em; opacity:0.4;"><i class="fas fa-trophy"></i></div>' +
                '<div style="margin-top:8px;">' + _esc(_tt('tourn.none', 'No tournaments yet.')) + '</div></div>';
            return;
        }

        var q = (_toSearchQuery || '').trim().toLowerCase();
        var list = _tournListCache;
        if (q) {
            list = list.filter(function (t) {
                return ((t.name || '').toLowerCase().indexOf(q) !== -1) ||
                       ((t.game_title || '').toLowerCase().indexOf(q) !== -1) ||
                       (toFormatLabel(t.format) || '').toLowerCase().indexOf(q) !== -1;
            });
        }

        var body = (!list.length && q)
            ? '<div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' + _esc(_tt('tourn.searchNone', 'No tournaments match your search.')) + '</div>'
            : '<div class="to-list-scroll" style="max-height:70vh; overflow-y:auto; -webkit-overflow-scrolling:touch;">' + list.map(_tournamentCardHtml).join('') + '</div>';

        host.innerHTML = seriesBtn + body;
        try { if (typeof window.applyTranslations === 'function') window.applyTranslations(host); } catch (e) {}
    }

    /* montează butoanele icon search + export lângă butonul „New tournament”,
       în stilul Game Catalog (.tab-head-btn) + un panou .sess-panel deasupra listei. */
    function _toMountHeaderTools() {
        try {
            var host = document.getElementById('tournamentsList');
            var searchBtnHtml = '<button type="button" class="tab-head-btn" id="toTile-search" title="' + _attr(_tt('tourn.search', 'Search')) + '" aria-label="' + _attr(_tt('tourn.search', 'Search')) + '" onclick="_toToggleSearchPanel(this)"><i class="fas fa-search"></i></button>';
            var exportBtnHtml = '<button type="button" class="tab-head-btn" title="' + _attr(_tt('tourn.export', 'Export')) + '" aria-label="' + _attr(_tt('tourn.export', 'Export')) + '" onclick="_toExport()"><i class="fas fa-file-export"></i></button>';

            // grup de butoane — o singură dată (fie în header lângă New, fie deasupra listei pt. membri)
            if (!document.getElementById('toHeaderTools') && !document.getElementById('toListTools')) {
                if (!document.getElementById('toHeaderToolsCss')) {
                    var stl = document.createElement('style');
                    stl.id = 'toHeaderToolsCss';
                    stl.textContent =
                        '#toHeaderTools{display:inline-flex;gap:8px;align-items:center;flex-wrap:wrap;}' +
                        'button[onclick*="openTournamentEditor"]:not(#toNewDesktop):not(#toNewMobile){display:none !important;}' +
                        '#toNewMobile{display:none;}' +
                        '@media(max-width:600px){#toNewDesktop{display:none;}#toNewMobile{display:inline-flex;}}';
                    document.head.appendChild(stl);
                }
                var newBtn = document.querySelector('button[onclick*="openTournamentEditor"]:not(#toNewDesktop):not(#toNewMobile)');
                if (newBtn && newBtn.parentNode) {
                    // Moderator+: grup în header cu replica butonului New + search + export
                    var cls = newBtn.className || 'btn btn-primary';
                    var inlineStyle = (newBtn.getAttribute('style') || '');
                    var label = _tt('tourn.new', 'New tournament');
                    var wrap = document.createElement('span');
                    wrap.id = 'toHeaderTools';
                    wrap.innerHTML =
                        '<button type="button" id="toNewDesktop" class="' + _attr(cls) + '" style="' + _attr(inlineStyle) + '" onclick="openTournamentEditor()"><i class="fas fa-plus"></i> ' + _esc(label) + '</button>' +
                        '<button type="button" id="toNewMobile" class="tab-head-btn accent" title="' + _attr(label) + '" aria-label="' + _attr(label) + '" onclick="openTournamentEditor()"><i class="fas fa-plus"></i></button>' +
                        searchBtnHtml + exportBtnHtml;
                    newBtn.parentNode.insertBefore(wrap, newBtn.nextSibling);
                } else if (host && host.parentNode) {
                    // Membru (fără buton New): doar search + export, deasupra listei, aliniate dreapta
                    var bar = document.createElement('div');
                    bar.id = 'toListTools';
                    bar.style.cssText = 'display:flex; justify-content:flex-end; gap:8px; margin-bottom:10px;';
                    bar.innerHTML = searchBtnHtml + exportBtnHtml;
                    host.parentNode.insertBefore(bar, host);
                }
            }
            // panou de căutare deasupra listei (.sess-panel, ca la Game Catalog)
            if (host && host.parentNode && !document.getElementById('toPanel-search')) {
                var panel = document.createElement('div');
                panel.id = 'toPanel-search';
                panel.className = 'sess-panel hidden';
                panel.innerHTML =
                    '<label for="toSearchInput"><i class="fas fa-search"></i> ' + _esc(_tt('tourn.search', 'Search')) + '</label>' +
                    '<input type="search" id="toSearchInput" class="form-control" placeholder="' + _attr(_tt('tourn.searchPh', 'Search tournaments…')) + '" oninput="_toSearchInput(this.value)">';
                host.parentNode.insertBefore(panel, host);
            }
            return true;
        } catch (e) { _tlog('_toMountHeaderTools', e); return false; }
    }

    /* deschide/închide panoul de căutare (reutilizează toggleTilePanel global). */
    function _toToggleSearchPanel(btn) {
        try {
            if (typeof toggleTilePanel === 'function') toggleTilePanel('toPanel-search', btn);
            else { var p0 = document.getElementById('toPanel-search'); if (p0) p0.classList.toggle('hidden'); }
        } catch (e) {}
        var panel = document.getElementById('toPanel-search');
        var inp = document.getElementById('toSearchInput');
        var open = panel && !panel.classList.contains('hidden');
        _toSearchOpen = !!open;
        if (open) { if (inp) { try { inp.focus(); } catch (e) {} } }
        else { if (inp) inp.value = ''; _toSearchQuery = ''; _toPaintList(); }
    }

    function _toSearchInput(v) {
        _toSearchQuery = v || '';
        _toPaintList();          // panoul e frate cu lista, deci input-ul își păstrează focusul
    }

    /* export al turneelor curente (respectă filtrul de căutare).
       Preferă .xlsx real (coloane/rânduri garantate); fallback CSV cu sep=; */
    async function _toExport() {
        try {
            /* Batch 2: SheetJS nu mai e încărcat la pornire — îl aducem acum.
               Dacă încărcarea eșuează, NU afișăm eroare: codul de mai jos are deja
               fallback pe CSV, deci utilizatorul primește tot un fișier. */
            try { await window.ensureLib('xlsx'); } catch (e) { }
            var q = (_toSearchQuery || '').trim().toLowerCase();
            var list = _tournListCache.filter(function (t) {
                if (!q) return true;
                return ((t.name || '').toLowerCase().indexOf(q) !== -1) ||
                       ((t.game_title || '').toLowerCase().indexOf(q) !== -1) ||
                       (toFormatLabel(t.format) || '').toLowerCase().indexOf(q) !== -1;
            });
            if (!list.length) { if (typeof showNotification === 'function') showNotification(_tt('tourn.exportEmpty', 'Nothing to export.'), 'error'); return; }

            var head = [
                _tt('tourn.f.name', 'Name'), _tt('tourn.f.game', 'Game'), _tt('tourn.f.format', 'Format'),
                _tt('tourn.status', 'Status'), _tt('tourn.f.minP', 'Min'), _tt('tourn.f.maxP', 'Max'),
                _tt('tourn.deadline', 'Deadline'), _tt('tourn.created', 'Created')
            ];
            function dateStr(v) { if (!v) return ''; try { return new Date(v).toLocaleDateString(); } catch (e) { return String(v); } }
            var rows = list.map(function (t) {
                return [
                    t.name || '', t.game_title || '', toFormatLabel(t.format), toStatusLabel(t.status),
                    (t.min_participants != null ? t.min_participants : ''),
                    (t.max_participants != null ? t.max_participants : ''),
                    dateStr(t.registration_deadline), dateStr(t.created_at)
                ];
            });
            var aoa = [head].concat(rows);
            var fname = 'turnee-' + new Date().toISOString().slice(0, 10);

            var XLSX = (typeof window !== 'undefined') ? window.XLSX : null;
            if (XLSX && XLSX.utils) {
                var ws = XLSX.utils.aoa_to_sheet(aoa);
                // lățimi de coloană rezonabile
                ws['!cols'] = [{ wch: 26 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 6 }, { wch: 6 }, { wch: 14 }, { wch: 14 }];
                var wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Turnee');
                var arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
                _toDownloadBlob(new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fname + '.xlsx');
                return;
            }

            // fallback CSV: sep=; (Excel RO/EU desparte pe punct-și-virgulă) + BOM
            function esc(v) { v = (v == null) ? '' : String(v); return /[";\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; }
            var csv = '\ufeffsep=;\n' + aoa.map(function (r) { return r.map(esc).join(';'); }).join('\n');
            _toDownloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), fname + '.csv');
        } catch (e) { _tlog('_toExport', e); _toToastErr(e); }
    }

    function _toDownloadBlob(blob, filename) {
        try {
            var url = URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            setTimeout(function () { try { document.body.removeChild(a); URL.revokeObjectURL(url); } catch (e) {} }, 0);
        } catch (e) { _tlog('_toDownloadBlob', e); }
    }

    /* ============================================================
       UI — PAGINA DE TURNEU (Batch 3c)
       Modal cu detaliu: antet, participanți (registered + waitlist),
       înscriere self-service (Membru+), acțiuni de status (Moderator+).
       Bracket-ul vizual + raportare rezultate vin în Batch 4.
       ============================================================ */
    var _toCurrentPageId = null;
    var _toPageData = null;                 // cache date pagină (pt. share sincron)
    var _toStandView = 'uniform';           // vizualizare clasament: 'uniform' (plasament) | 'raw' (scor)

    /* închide pagina de turneu + resetează starea (previne render în modal fantomă). */
    function closeTournamentPage() {
        _toCurrentPageId = null;
        var m = document.querySelector('.modal.active[data-tourn-page]');
        if (m) m.remove();
    }

    async function openTournamentPage(id) {
        _toCurrentPageId = id;
        if (!_tournChannel) { try { subscribeRealtimeTournaments(); } catch (e) {} }
        // curăță ORICE modal de turneu rămas (nu doar primul) ca să nu existe #toPageBody duplicat
        var stale = document.querySelectorAll('.modal.active[data-tourn-page]');
        for (var i = 0; i < stale.length; i++) stale[i].remove();

        var modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.tournPage = String(id);
        modal.innerHTML = '<div class="modal-content"><div class="modal-header">' +
            '<h2><i class="fas fa-trophy"></i> ' + _esc(_tt('tourn.title', 'Tournaments')) + '</h2>' +
            '<button class="modal-close" onclick="closeTournamentPage()">×</button></div>' +
            '<div class="to-page-body"><div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' +
            _esc(_tt('tourn.loading', 'Loading…')) + '</div></div></div>';
        document.body.appendChild(modal);
        await _renderTournamentPageBody(id);
    }

    async function _renderTournamentPageBody(id) {
        // container scoped pe modalul curent (nu getElementById global -> fără duplicate)
        var modal = document.querySelector('.modal.active[data-tourn-page="' + String(id).replace(/"/g, '') + '"]')
                 || document.querySelector('.modal.active[data-tourn-page]');
        if (!modal) return;
        var body = modal.querySelector('.to-page-body');
        if (!body) return;
        try {
            await _renderTournamentPageBodyInner(id, body);
        } catch (e) {
            _tlog('_renderTournamentPageBody', e);
            // NICIODATĂ blocat pe „Loading”: arătăm eroare + reîncercare
            body.innerHTML = '<div style="padding:20px; text-align:center; color:var(--danger,#e74c3c);">' +
                _esc(_tt('tourn.err.generic', 'Something went wrong.')) +
                '<br><button class="btn btn-sm btn-secondary" style="margin-top:10px;" onclick="_toRetryPage()">' +
                _esc(_tt('common.retry', 'Retry')) + '</button></div>';
        }
    }

    function _toRetryPage() {
        if (_toCurrentPageId) _renderTournamentPageBody(_toCurrentPageId);
    }

    async function _renderTournamentPageBodyInner(id, body) {
        var data = await toLoadTournament(id);
        if (!data || !data.tournament) {
            body.innerHTML = '<div style="padding:20px; text-align:center; color:var(--danger,#e74c3c);">' +
                _esc(_tt('tourn.err.TOURNAMENT_NOT_FOUND', 'Not found.')) + '</div>';
            return;
        }
        _toPageData = data;   // cache pentru share sincron
        var t = data.tournament;
        var parts = data.participants || [];
        var myId = (typeof state !== 'undefined' && state.currentUser) ? state.currentUser.id : null;

        var registered = parts.filter(function (p) { return p.status === 'registered' || p.status === 'checked_in'; });
        var waitlisted = parts.filter(function (p) { return p.status === 'waitlisted'; });
        var mine = parts.find(function (p) { return String(p.profile_id) === String(myId); });

        var isMod = (typeof can === 'function') && can('tournament.manage');
        var isMember = (typeof can === 'function') && can('tournament.join');
        var st = t.status;

        // ---- antet ----
        var meta = [];
        if (t.game_title) meta.push('<i class="fas fa-dice"></i> ' + _esc(t.game_title));
        meta.push('<i class="fas fa-sitemap"></i> ' + _esc(toFormatLabel(t.format)));
        if (t.registration_deadline) {
            try { meta.push('<i class="fas fa-hourglass-half"></i> ' + _esc(new Date(t.registration_deadline).toLocaleString())); } catch (e) {}
        }
        if (t.entry_fee != null) meta.push('<i class="fas fa-ticket"></i> ' + _esc(String(t.entry_fee)));
        var prizeTxt = (t.prizes && t.prizes.text) ? t.prizes.text : '';
        if (prizeTxt) meta.push('<i class="fas fa-gift"></i> ' + _esc(prizeTxt));

        var capTxt = t.max_participants ? (registered.length + '/' + t.max_participants) : String(registered.length);

        var html = '' +
            '<div style="display:flex; justify-content:space-between; align-items:flex-start; gap:10px; flex-wrap:wrap;">' +
              '<div style="font-size:1.15em; font-weight:700; overflow-wrap:anywhere;">' + _esc(t.name) + '</div>' +
              '<div style="flex:0 0 auto; display:flex; align-items:center; gap:8px;">' +
                '<button class="btn btn-sm btn-secondary" title="' + _attr(_tt('common.share', 'Share')) + '" onclick="openShareMenu(\'tournament\',\'' + _attr(t.id) + '\',this)"><i class="fas fa-share-nodes"></i></button>' +
                '<span style="padding:3px 10px; border-radius:12px; font-size:0.75em; font-weight:700; ' + _statusBadgeStyle(st) + '">' + _esc(toStatusLabel(st)) + '</span>' +
              '</div>' +
            '</div>' +
            '<div style="font-size:0.84em; color:var(--text-secondary,#bbb); margin-top:8px; display:flex; gap:14px; flex-wrap:wrap;">' + meta.join(' ') + '</div>';

        // ---- badge campion (turneu finalizat) ----
        if (st === 'completed' && t.winner_id) {
            var champName = (typeof displayNameById === 'function') ? (displayNameById(t.winner_id) || String(t.winner_id).slice(0, 8)) : String(t.winner_id).slice(0, 8);
            html += '<div style="margin-top:12px; padding:12px 14px; border-radius:10px; border:1px solid var(--accent-gold,#ffd700); background:rgba(255,215,0,0.08); display:flex; align-items:center; gap:10px;">' +
                '<i class="fas fa-trophy" style="color:var(--accent-gold,#ffd700); font-size:1.3em;"></i>' +
                '<div><div style="font-size:0.72em; opacity:0.7; text-transform:uppercase; letter-spacing:0.03em;">' + _esc(_tt('tourn.champion', 'Champion')) + '</div>' +
                '<div style="font-weight:700; font-size:1.05em;">' + _esc(champName) + '</div></div></div>';
        }

        // ---- butoane self-service înscriere (doar în registration) ----
        if (st === 'registration' && isMember) {
            if (mine && (mine.status === 'registered' || mine.status === 'checked_in')) {
                html += '<button class="btn btn-secondary btn-sm" style="margin-top:14px;" onclick="_toLeaveFromPage()"><i class="fas fa-user-minus"></i> ' + _esc(_tt('tourn.leave', 'Withdraw')) + '</button>';
            } else if (mine && mine.status === 'waitlisted') {
                html += '<div style="margin-top:14px; display:flex; align-items:center; gap:10px; flex-wrap:wrap;">' +
                    '<span style="padding:4px 10px; border-radius:10px; background:rgba(241,196,15,0.15); color:#f1c40f; font-size:0.8em; font-weight:700;"><i class="fas fa-hourglass-half"></i> ' + _esc(_tt('tourn.waitlisted', 'On waitlist')) + '</span>' +
                    '<button class="btn btn-secondary btn-sm" onclick="_toLeaveFromPage()"><i class="fas fa-user-minus"></i> ' + _esc(_tt('tourn.leave', 'Withdraw')) + '</button></div>';
            } else {
                html += '<button class="btn btn-primary btn-sm" style="margin-top:14px;" onclick="_toJoinFromPage()"><i class="fas fa-user-plus"></i> ' + _esc(_tt('tourn.join', 'Join')) + '</button>';
            }
        }

        // ---- acțiuni de status pentru Moderator+ ----
        if (isMod) {
            var actions = [];
            if (st === 'draft') actions.push(['registration', _tt('tourn.act.openReg', 'Open registration'), 'fa-door-open']);
            if (st === 'registration') {
                actions.push(['draft', _tt('tourn.act.backDraft', 'Back to draft'), 'fa-rotate-left']);
                actions.push(['live', _tt('tourn.act.start', 'Start tournament'), 'fa-play']);
            }
            // „Finalizează" -> dialog dedicat (câștigător + tie-break uman + bonus Admin)
            if (st === 'live') {
                html += '<div style="margin-top:14px; padding-top:12px; border-top:1px solid var(--border,#333); display:flex; gap:8px; flex-wrap:wrap;">' +
                    '<button class="btn btn-sm" onclick="_toOpenFinalize()"><i class="fas fa-flag-checkered"></i> ' + _esc(_tt('tourn.act.finish', 'Finish')) + '</button></div>';
            }
            // „Redeschide" -> doar Admin, din completed
            if (st === 'completed' && _toIsAdmin()) {
                html += '<div style="margin-top:14px; padding-top:12px; border-top:1px solid var(--border,#333); display:flex; gap:8px; flex-wrap:wrap;">' +
                    '<button class="btn btn-sm btn-secondary" onclick="_toReopenFromPage()"><i class="fas fa-rotate-left"></i> ' + _esc(_tt('tourn.act.reopen', 'Reopen')) + '</button></div>';
            }
            if (actions.length) {
                html += '<div style="margin-top:14px; padding-top:12px; border-top:1px solid var(--border,#333); display:flex; gap:8px; flex-wrap:wrap;">' +
                    actions.map(function (a) {
                        return '<button class="btn btn-sm" onclick="_toSetStatusFromPage(\'' + _attr(a[0]) + '\')"><i class="fas ' + a[2] + '"></i> ' + _esc(a[1]) + '</button>';
                    }).join('') + '</div>';
            }
        }

        // ---- pagină publică (DOAR creator sau Admin) ----
        var canPublish = (myId && String(t.created_by) === String(myId)) || _toIsAdmin();
        if (canPublish) {
            var pubLink = (t.public_enabled && t.public_code)
                ? (location.origin + '/public-tournament.html?c=' + encodeURIComponent(t.public_code)) : '';
            html += '<div style="margin-top:14px; padding-top:12px; border-top:1px solid var(--border,#333);">' +
                '<div style="font-weight:700; margin-bottom:8px;"><i class="fas fa-globe"></i> ' + _esc(_tt('tourn.public.title', 'Public page')) + '</div>';
            if (pubLink) {
                html += '<div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center;">' +
                        '<input type="text" class="form-control" id="toPublicLink" readonly value="' + _attr(pubLink) + '" style="flex:1 1 220px; min-width:0;" onclick="this.select()">' +
                        '<button type="button" class="btn btn-sm" onclick="_toCopyPublicLink()"><i class="fas fa-copy"></i> ' + _esc(_tt('common.copy', 'Copy')) + '</button>' +
                        '<button type="button" class="btn btn-sm btn-secondary" onclick="_toMakePrivate(\'' + _attr(id) + '\')"><i class="fas fa-lock"></i> ' + _esc(_tt('tourn.public.makePrivate', 'Make private')) + '</button>' +
                    '</div>' +
                    '<small style="display:block; opacity:0.6; margin-top:6px;">' + _esc(_tt('tourn.public.hint', 'Anyone with this link can view the bracket, standings and champion — no login. Making it private revokes the link.')) + '</small>';
            } else {
                html += '<button type="button" class="btn btn-sm" onclick="_toMakePublic(\'' + _attr(id) + '\')"><i class="fas fa-globe"></i> ' + _esc(_tt('tourn.public.makePublic', 'Make public')) + '</button>' +
                    '<small style="display:block; opacity:0.6; margin-top:6px;">' + _esc(_tt('tourn.public.hintOff', 'Generates a read-only public link (bracket, standings, champion). You can revoke it anytime.')) + '</small>';
            }
            html += '</div>';
        }

        // ---- listă participanți (nume pe id §23.1) ----
        function nameOf(pid) {
            var n = (typeof displayNameById === 'function') ? displayNameById(pid) : '';
            return n || String(pid).slice(0, 8);
        }
        function partRow(p, idx) {
            var meMark = (String(p.profile_id) === String(myId)) ? ' <span style="opacity:0.6;">(' + _esc(_tt('tourn.you', 'you')) + ')</span>' : '';
            return '<div style="display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--border,rgba(255,255,255,0.05));">' +
                '<span style="width:22px; text-align:right; opacity:0.5; font-size:0.85em;">' + (idx + 1) + '</span>' +
                _toPlayerChip(p.profile_id, { suffix: meMark }) + '</div>';
        }

        html += '<div style="margin-top:18px;">' +
            '<div style="font-weight:700; margin-bottom:6px;"><i class="fas fa-users"></i> ' + _esc(_tt('tourn.participants', 'Participants')) + ' <span style="opacity:0.6; font-weight:normal;">(' + _esc(capTxt) + ')</span></div>';
        if (registered.length) html += registered.map(partRow).join('');
        else html += '<div style="opacity:0.6; padding:8px 0;">' + _esc(_tt('tourn.noParticipants', 'No participants yet.')) + '</div>';

        if (waitlisted.length) {
            html += '<div style="font-weight:700; margin:14px 0 6px;"><i class="fas fa-hourglass-half"></i> ' + _esc(_tt('tourn.waitlist', 'Waitlist')) + ' <span style="opacity:0.6; font-weight:normal;">(' + waitlisted.length + ')</span></div>';
            html += waitlisted.map(partRow).join('');
        }
        html += '</div>';

        // ---- bracket: buton generare (Moderator+) + afișare runde/mese ----
        var rounds = data.rounds || [];
        if (st === 'live' || st === 'completed' || rounds.length) {
            html += '<div style="margin-top:18px; padding-top:12px; border-top:1px solid var(--border,#333);">';
            html += '<div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:10px;">' +
                '<div style="font-weight:700;"><i class="fas fa-sitemap"></i> ' + _esc(_tt('tourn.bracket', 'Bracket')) + '</div>';
            // buton generare pentru Moderator: round_robin sau tables (mese de N), în registration/live
            if (isMod && (t.format === 'round_robin' || t.format === 'tables') && (st === 'registration' || st === 'live')) {
                var genLabel = rounds.length ? _tt('tourn.regenBracket', 'Regenerate bracket') : _tt('tourn.genBracket', 'Generate bracket');
                html += '<button class="btn btn-sm" onclick="_toGenerateBracketFromPage()"><i class="fas fa-diagram-project"></i> ' + _esc(genLabel) + '</button>';
            }
            // single-elim: rundă 1 (dacă nu există) sau „runda următoare” (avansare)
            if (isMod && t.format === 'single_elim' && (st === 'registration' || st === 'live')) {
                if (!rounds.length) {
                    html += '<button class="btn btn-sm" onclick="_toGenerateBracketFromPage()"><i class="fas fa-diagram-project"></i> ' + _esc(_tt('tourn.genBracket', 'Generate bracket')) + '</button>';
                } else if (st === 'live') {
                    html += '<button class="btn btn-sm" onclick="_toAdvanceElimFromPage()"><i class="fas fa-forward-step"></i> ' + _esc(_tt('tourn.nextRound', 'Generate next round')) + '</button>';
                }
            }
            // Swiss: rundă 1 (dacă nu există) sau „runda următoare” (după clasament)
            if (isMod && t.format === 'swiss' && (st === 'registration' || st === 'live')) {
                if (!rounds.length) {
                    html += '<button class="btn btn-sm" onclick="_toGenerateBracketFromPage()"><i class="fas fa-diagram-project"></i> ' + _esc(_tt('tourn.genBracket', 'Generate bracket')) + '</button>';
                } else if (st === 'live') {
                    html += '<button class="btn btn-sm" onclick="_toAdvanceSwissFromPage()"><i class="fas fa-forward-step"></i> ' + _esc(_tt('tourn.nextRound', 'Generate next round')) + '</button>';
                }
            }
            html += '</div>';

            if (rounds.length) {
                html += _bracketHtml(rounds, myId, t, isMod, st);
            } else {
                html += '<div style="padding:14px; border:1px dashed var(--border,#444); border-radius:10px; text-align:center; color:var(--text-secondary,#bbb); font-size:0.88em;">' +
                    _esc(_tt('tourn.noBracket', 'No bracket generated yet.')) + '</div>';
            }
            html += '</div>';
        }

        // ---- clasament de turneu live (când e live/completed sau există runde) ----
        if ((st === 'live' || st === 'completed') && (data.rounds || []).length) {
            html += _standingsHtml(data, t.id);
        }

        body.innerHTML = html;
    }

    /* randează rundele + mesele (nume pe id §23.1, escape complet §9.1). */
    function _bracketHtml(rounds, myId, tournament, isMod, tournStatus) {
        function nameOf(pid) {
            if (pid == null) return null;
            var n = (typeof displayNameById === 'function') ? displayNameById(pid) : '';
            return n || String(pid).slice(0, 8);
        }
        // pentru Round Robin tur-retur, calculăm pragul dintre tur și retur
        var legs = (tournament && tournament.settings && tournament.settings.legs === 2) ? 2 : 1;
        var isRR = tournament && tournament.format === 'round_robin';
        var halfRounds = (isRR && legs === 2) ? Math.floor(rounds.length / 2) : 0;
        var canReport = isMod && tournStatus === 'live';

        return rounds.map(function (rd, idx) {
            var baseLabel = rd.label || (_tt('tourn.round', 'Round') + ' ' + rd.round_no);
            var roundLabelHtml = _esc(baseLabel);
            // sufix tur/retur doar la RR cu legs=2 (HTML deja sigur: text escapat + markup static)
            if (halfRounds > 0) {
                var legTag = (idx < halfRounds) ? _tt('tourn.leg.first', 'first leg') : _tt('tourn.leg.second', 'return leg');
                roundLabelHtml += ' <span style="opacity:0.6; font-weight:normal; font-size:0.85em;">(' + _esc(legTag) + ')</span>';
            }
            var tables = (rd.tables || []).map(function (tb) {
                // are jucători reali? (nu doar bye)
                var realPlayers = (tb.seats || []).filter(function (s) { return s.profile_id != null && !s.is_bye; });
                var seatsHtml = (tb.seats || []).map(function (s) {
                    if (s.is_bye || s.profile_id == null) {
                        return '<div style="opacity:0.5; font-style:italic;">— ' + _esc(_tt('tourn.bye', 'bye')) + ' —</div>';
                    }
                    var me = (String(s.profile_id) === String(myId)) ? ' style="font-weight:700; color:var(--accent-primary,#ff6b35);"' : '';
                    if (s.no_show) {
                        return '<div' + me + '>' + _toPlayerChip(s.profile_id, { strike: true, suffix: ' <span style="opacity:0.7; font-size:0.8em; color:var(--warning,#f5b301);">(' + _esc(_tt('tourn.noShow', 'No-show')) + ')</span>' }) + '</div>';
                    }
                    var place = (s.placement != null) ? ' <span style="opacity:0.6; font-size:0.85em;">#' + s.placement + '</span>' : '';
                    return '<div' + me + '>' + _toPlayerChip(s.profile_id, { suffix: place }) + '</div>';
                }).join('');
                var reported = (tb.status === 'reported' || tb.status === 'confirmed');
                var tblStatus = (tb.status && tb.status !== 'pending')
                    ? '<span style="font-size:0.72em; opacity:0.6; margin-left:6px;">(' + _esc(_tt('tourn.tstatus.' + tb.status, tb.status)) + ')</span>' : '';
                // buton raportare/editare doar pt Moderator, turneu live, masă cu jucători reali
                var reportBtn = '';
                if (canReport && realPlayers.length) {
                    var lbl = reported ? _tt('tourn.editResult', 'Edit result') : _tt('tourn.reportResult', 'Enter result');
                    var ic = reported ? 'fa-pen' : 'fa-flag-checkered';
                    reportBtn = '<button class="btn btn-sm ' + (reported ? 'btn-secondary' : '') + '" style="margin-top:8px; width:100%;" onclick="_toOpenReport(\'' + _attr(tb.id) + '\')"><i class="fas ' + ic + '"></i> ' + _esc(lbl) + '</button>';
                }
                return '<div style="flex:1 1 180px; min-width:160px; border:1px solid var(--border,#333); border-radius:8px; padding:8px 10px; background:var(--bg-secondary,rgba(255,255,255,0.02));">' +
                    '<div style="font-size:0.75em; opacity:0.6; margin-bottom:4px;">' + _esc(_tt('tourn.table', 'Table')) + ' ' + tb.table_no + tblStatus + '</div>' +
                    seatsHtml + reportBtn + '</div>';
            }).join('');
            return '<div style="margin-bottom:14px;">' +
                '<div style="font-weight:600; font-size:0.9em; margin-bottom:6px; opacity:0.85;">' + roundLabelHtml + '</div>' +
                '<div style="display:flex; gap:10px; flex-wrap:wrap;">' + tables + '</div></div>';
        }).join('');
    }

    /* ---- Formular de raportare rezultat masă (Moderator+) ---- */
    async function _toOpenReport(tableId) {
        if (!_toCurrentPageId) return;
        var data = await toLoadTournament(_toCurrentPageId);
        if (!data) return;
        // găsim masa + locurile ei
        var tbl = null;
        (data.rounds || []).forEach(function (rd) {
            (rd.tables || []).forEach(function (tb) { if (String(tb.id) === String(tableId)) tbl = tb; });
        });
        if (!tbl) return;
        var seats = (tbl.seats || []).filter(function (s) { return s.profile_id != null && !s.is_bye; });
        if (!seats.length) return;

        function nameOf(pid) {
            var n = (typeof displayNameById === 'function') ? displayNameById(pid) : '';
            return n || String(pid).slice(0, 8);
        }

        var rowsHtml = seats.map(function (s, i) {
            var scoreVal = (s.raw_score != null) ? s.raw_score : '';
            var isNs = !!s.no_show;
            return '<div class="to-seat-row" style="display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--border,rgba(255,255,255,0.06));">' +
                '<div style="flex:1; min-width:0;" data-pid="' + _attr(s.profile_id) + '">' + _toPlayerChip(s.profile_id) + '</div>' +
                '<label style="font-size:0.72em; opacity:0.7; display:flex; align-items:center; gap:4px; cursor:pointer;" title="' + _attr(_tt('tourn.noShowHint', 'Player did not show up')) + '">' +
                  '<input type="checkbox" class="to-report-noshow" data-pid="' + _attr(s.profile_id) + '"' + (isNs ? ' checked' : '') + ' onchange="_toToggleNoShow(this)"> ' + _esc(_tt('tourn.noShow', 'No-show')) + '</label>' +
                '<span class="form-section-label" style="font-size:0.75em; opacity:0.7;">' + _esc(_tt('tourn.score', 'Score')) + '</span>' +
                '<input type="number" step="any" class="form-control to-report-score" data-pid="' + _attr(s.profile_id) + '" value="' + _attr(scoreVal) + '" placeholder="0" style="width:84px;"' + (isNs ? ' disabled' : '') + ' oninput="_toRenderTieBar()">' +
                '</div>';
        }).join('');

        var modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.toReport = String(tableId);
        modal.style.zIndex = '2147483601';

        // selector session scoring type — apare doar dacă clubul a activat uniformizarea
        var cfg = _scoringCfg();
        var defMode = _clubDefaultMode();
        var modeSelector = '';
        if (cfg.enabled) {
            modeSelector =
                '<div class="form-group" style="margin-top:6px;"><label for="toReportMode" style="display:flex; align-items:center; gap:6px;">' +
                '<i class="fas fa-scale-balanced"></i> ' + _esc(_tt('tourn.scoreMode', 'Session scoring type')) + '</label>' +
                '<select id="toReportMode" class="form-control to-report-mode" onchange="_toRenderTieBar()">' +
                  '<option value="uniform"' + (defMode === 'uniform' ? ' selected' : '') + '>' + _esc(_tt('tourn.mode.uniform', 'Uniformized (Club Points by placement)')) + '</option>' +
                  '<option value="raw"' + (defMode === 'raw' ? ' selected' : '') + '>' + _esc(_tt('tourn.mode.raw', 'By score')) + '</option>' +
                '</select>' +
                '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.mode.hint', 'Uniformized: Club Points are awarded by placement, per the Admin template.')) + '</small></div>';
        }

        modal.innerHTML = '<div class="modal-content" style="max-width:520px;"><div class="modal-header">' +
            '<h2><i class="fas fa-flag-checkered"></i> ' + _esc(_tt('tourn.reportResult', 'Enter result')) + '</h2>' +
            '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>' +
            '<p style="font-size:0.82em; color:var(--text-secondary,#bbb);">' + _esc(_tt('tourn.reportHint2', 'Enter each player\'s score. Ties are broken below, per the game rules.')) + '</p>' +
            rowsHtml + modeSelector +
            '<div class="to-walkover-hint" style="display:none; margin-top:10px; padding:8px 10px; border-radius:8px; background:rgba(255,180,0,0.12); color:var(--warning,#f5b301); font-size:0.82em;"></div>' +
            '<div class="to-tiebar" style="margin-top:12px;"></div>' +
            '<button class="btn btn-primary" style="margin-top:14px;" onclick="_toSaveReport(\'' + _attr(tableId) + '\')"><i class="fas fa-save"></i> ' + _esc(_tt('common.save', 'Save')) + '</button>' +
            '</div>';
        document.body.appendChild(modal);
        _toTieBreaks = {};       // reset departajări la fiecare deschidere
        _toRenderTieBar();       // afișează bara dacă deja există egalități
        _toRenderWalkoverHint(); // afișează avertismentul dacă deja sunt absenți
    }

    /* stare departajare pentru modalul de raportare curent (scoped, nu globalul sesiunii). */
    var _toTieBreaks = {};

    /* citește scorurile din modalul de raportare curent -> {pid: număr}. Sare peste absenți. */
    function _toReadReportScores() {
        var modal = document.querySelector('.modal.active[data-to-report]');
        var out = {};
        if (!modal) return out;
        var ns = _toReadNoShows();
        Array.prototype.forEach.call(modal.querySelectorAll('.to-report-score'), function (el) {
            var pid = el.getAttribute('data-pid');
            if (ns.indexOf(pid) >= 0) return;      // absent -> nu intră în scoruri
            var v = Number(el.value);
            out[pid] = isNaN(v) ? 0 : v;
        });
        return out;
    }

    /* id-urile marcate „absent” în modalul curent. */
    function _toReadNoShows() {
        var modal = document.querySelector('.modal.active[data-to-report]');
        var out = [];
        if (!modal) return out;
        Array.prototype.forEach.call(modal.querySelectorAll('.to-report-noshow'), function (el) {
            if (el.checked) out.push(el.getAttribute('data-pid'));
        });
        return out;
    }

    /* toggle „absent”: dezactivează inputul de scor, reface bara + avertismentul de walkover. */
    function _toToggleNoShow(cb) {
        try {
            var row = cb.closest('.to-seat-row');
            var scoreEl = row && row.querySelector('.to-report-score');
            if (scoreEl) { scoreEl.disabled = !!cb.checked; if (cb.checked) scoreEl.value = ''; }
            _toRenderTieBar();
            _toRenderWalkoverHint();
        } catch (e) { _tlog('_toToggleNoShow', e); }
    }

    /* dacă rămâne <=1 jucător real, arată că masa se va încheia prin walkover. */
    function _toRenderWalkoverHint() {
        var modal = document.querySelector('.modal.active[data-to-report]');
        if (!modal) return;
        var box = modal.querySelector('.to-walkover-hint');
        if (!box) return;
        var total = modal.querySelectorAll('.to-report-noshow').length;
        var absent = _toReadNoShows().length;
        var real = total - absent;
        if (real <= 1 && total >= 2) {
            box.style.display = '';
            box.innerHTML = '<i class="fas fa-triangle-exclamation"></i> ' +
                _esc(real === 1 ? _tt('tourn.walkoverHint', 'Only one player left — the table will be won by walkover (no game session).')
                                : _tt('tourn.walkoverNone', 'No players left at this table.'));
        } else {
            box.style.display = 'none';
            box.innerHTML = '';
        }
    }

    /* randează bara de tie-break — REUTILIZEAZĂ detectScoreTies (sistemul din sesiuni).
       Apare doar în mod uniform, când există scoruri egale > 0. Simbol ⚔ va apărea în istoric. */
    function _toRenderTieBar() {
        var modal = document.querySelector('.modal.active[data-to-report]');
        if (!modal) return;
        var bar = modal.querySelector('.to-tiebar');
        if (!bar) return;
        var modeEl = modal.querySelector('.to-report-mode');
        var mode = modeEl ? modeEl.value : 'raw';
        // în raw nu departajăm (scorurile se însumează; egalitatea e permisă)
        if (mode !== 'uniform') { bar.innerHTML = ''; _toTieBreaks = {}; return; }

        var scores = _toReadReportScores();
        var ties = (typeof detectScoreTies === 'function')
            ? detectScoreTies(Object.keys(scores), scores) : [];
        // curăță departajările pentru cei care nu mai sunt la egalitate
        var tiedSet = {}; ties.forEach(function (g) { g.forEach(function (p) { tiedSet[p] = 1; }); });
        Object.keys(_toTieBreaks).forEach(function (k) { if (!tiedSet[k]) delete _toTieBreaks[k]; });
        if (!ties.length) { bar.innerHTML = ''; return; }

        var nm = function (id) { return (typeof displayNameById === 'function' && displayNameById(id)) || id; };
        bar.innerHTML =
            '<div style="border:1px solid var(--accent-gold,#ffd700); background:rgba(255,215,0,0.06); border-radius:10px; padding:12px;">' +
            '<div style="font-weight:700; display:flex; align-items:center; gap:8px;"><i class="fas fa-khanda" style="color:var(--accent-gold,#ffd700);"></i> ' + _esc(_tt('session.tieBreakTitle', 'Tie-breaker')) + '</div>' +
            '<small style="color:var(--text-secondary); display:block; margin:4px 0 10px;">' + _esc(_tt('session.tieBreakHint', 'Tap the player who won the tie-break per the game rules — they take the higher place.')) + '</small>' +
            ties.map(function (group) {
                var sc = scores[group[0]];
                return '<div style="margin-top:8px;">' +
                    '<div style="font-size:0.82em; color:var(--text-secondary); margin-bottom:6px;">' + _esc(_tt('session.tieAt', 'Tie at')) + ' <strong>' + _esc(sc) + '</strong> ' + _esc(_tt('hist.points', 'points')) + '</div>' +
                    '<div style="display:flex; flex-wrap:wrap; gap:8px;">' +
                    group.map(function (pid) {
                        var on = !!_toTieBreaks[pid];
                        return '<button type="button" class="btn btn-sm ' + (on ? 'btn-primary' : 'btn-secondary') + '" style="width:auto; flex:none;" onclick="_toToggleTieBreak(\'' + _attr(pid) + '\')">' +
                            '<i class="fas fa-' + (on ? 'trophy' : 'khanda') + '"></i> ' + _esc(nm(pid)) + '</button>';
                    }).join('') +
                    '</div></div>';
            }).join('') +
            '</div>';
    }

    /* marchează câștigătorul departajării în grupul lui (unul singur / grup). */
    function _toToggleTieBreak(pid) {
        var scores = _toReadReportScores();
        var ties = (typeof detectScoreTies === 'function') ? detectScoreTies(Object.keys(scores), scores) : [];
        var group = [];
        for (var i = 0; i < ties.length; i++) { if (ties[i].indexOf(pid) !== -1) { group = ties[i]; break; } }
        var wasOn = !!_toTieBreaks[pid];
        group.forEach(function (p) { delete _toTieBreaks[p]; });
        if (!wasOn) _toTieBreaks[pid] = true;
        _toRenderTieBar();
    }

    async function _toSaveReport(tableId) {
        var modal = document.querySelector('.modal.active[data-to-report]');
        if (!modal) return;
        var noShows = _toReadNoShows();
        var scoreInputs = modal.querySelectorAll('.to-report-score');
        var pids = [];
        var scores = {};
        Array.prototype.forEach.call(scoreInputs, function (el) {
            var pid = el.getAttribute('data-pid');
            if (!pid) return;
            if (noShows.indexOf(pid) >= 0) return;    // absent -> nu e jucător real
            var v = Number(el.value);
            scores[pid] = isNaN(v) ? 0 : v;
            pids.push(pid);
        });

        // WALKOVER: <=1 jucător real. Nu cerem scoruri; RPC-ul dă locul 1 celui rămas.
        if (pids.length <= 1) {
            if (!noShows.length) {
                if (typeof showNotification === 'function') showNotification(_tt('tourn.err.BAD_SEAT_RESULT', 'Invalid result.'), 'error');
                return;
            }
            var woSeats = pids.map(function (pid) { return { profile_id: pid, placement: 1 }; });
            var woRes = await toReportTable(tableId, { seats: woSeats, no_shows: noShows });
            if (!woRes) return;
            modal.remove();
            if (_toCurrentPageId) await _renderTournamentPageBody(_toCurrentPageId);
            return;
        }

        if (!pids.length) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.BAD_SEAT_RESULT', 'Invalid result.'), 'error');
            return;
        }

        var modeEl = modal.querySelector('.to-report-mode');
        var mode = modeEl ? modeEl.value : 'raw';

        // derivăm LOCURILE din scoruri + departajare (sistemul original din sesiuni),
        // respectând direcția scorului a jocului turneului (Z1-A)
        var tieBreaks = (mode === 'uniform') ? _toTieBreaks : {};
        var placements = _toDerivePlacements(pids, scores, tieBreaks, _toScoreDir());

        var seats = pids.map(function (pid) {
            return { profile_id: pid, placement: placements[pid], score: scores[pid] };
        });

        var results = { seats: seats };
        if (noShows.length) results.no_shows = noShows;
        if (mode === 'uniform') {
            results.score_mode = 'uniform';
            // club_points din perRank pe locul derivat (respectă departajarea)
            results.club_points = {};
            seats.forEach(function (s) { results.club_points[s.profile_id] = _ptsForPlace(s.placement, _scoringCfg().perRank); });
            // departajări -> ajung în meta.tieBreaks (⚔ în istoric)
            if (Object.keys(tieBreaks).length) results.tie_breaks = tieBreaks;
        } else {
            results.score_mode = 'raw';
        }

        var res = await toReportTable(tableId, results);
        if (!res) return;
        modal.remove();
        if (_toCurrentPageId) await _renderTournamentPageBody(_toCurrentPageId);
    }

    /* Direcția scorului a unui turneu (din jocul asociat, via catalog), 'high' implicit. */
    function _dirOfData(data) {
        try {
            var t = data && data.tournament; if (!t) return 'high';
            var cat = (typeof state !== 'undefined' && state.catalog) ? state.catalog : {};
            var g = null;
            if (t.game_id != null) {
                Object.keys(cat).some(function (nm) { if (String(cat[nm] && cat[nm].dbId) === String(t.game_id)) { g = cat[nm]; return true; } return false; });
            }
            if (!g && t.game_title && cat[t.game_title]) g = cat[t.game_title];
            return (g && g.scoreDir === 'low') ? 'low' : 'high';
        } catch (e) { return 'high'; }
    }
    /* Direcția turneului curent afișat (folosită la raportarea mesei). */
    function _toScoreDir() { return _dirOfData(_toPageData); }

    /* Derivă locuri din scoruri + departajare, coerent cu computeClubPoints.
       dir='high': scor desc (implicit). dir='low': scor asc (scor mic = locul 1).
       În grup de scor egal, cei cu departajare urcă; restul (egali) împart locul. */
    function _toDerivePlacements(pids, scores, tieBreaks, dir) {
        var lowWins = (dir === 'low');
        var num = function (v) { var n = Number(v); return isNaN(n) ? 0 : n; };
        var tb = tieBreaks || {};
        var sorted = pids.slice().sort(function (a, b) {
            var sa = num(scores[a]), sb = num(scores[b]);
            if (sb !== sa) return lowWins ? (sa - sb) : (sb - sa);
            return (tb[b] ? 1 : 0) - (tb[a] ? 1 : 0);
        });
        var place = {}, i = 0, cur = 1;
        while (i < sorted.length) {
            var si = num(scores[sorted[i]]);
            var k = i;
            while (k + 1 < sorted.length && num(scores[sorted[k + 1]]) === si) k++;
            var group = sorted.slice(i, k + 1);
            var flagged = group.filter(function (p) { return tb[p]; });
            var rest = group.filter(function (p) { return !tb[p]; });
            // câștigătorii departajării iau locurile de sus, în ordine
            flagged.forEach(function (p, idx) { place[p] = cur + idx; });
            // restul (egali reali) împart același loc (după cei departajați)
            rest.forEach(function (p) { place[p] = cur + flagged.length; });
            cur += group.length;
            i = k + 1;
        }
        return place;
    }

    /* ============================================================
       CLASAMENT DE TURNEU LIVE (Batch 4c) — pur, din rezultatele raportate.
       mode: 'uniform' = puncte după plasament la masă (locul 1 = N, … ultimul = 1);
             'raw'     = suma scorurilor brute introduse.
       FĂRĂ tie-break automat (regula jocului decide, per masă). Egalitățile pe
       total se marchează cu indicator; rangul e „competition ranking" (1,1,3).
       ============================================================ */
    function _computeStandings(data, mode) {
        var byPid = {};
        function ensure(pid) {
            if (!byPid[pid]) byPid[pid] = { profile_id: pid, points: 0, wins: 0, tables: 0 };
            return byPid[pid];
        }
        (data.rounds || []).forEach(function (rd) {
            (rd.tables || []).forEach(function (tb) {
                var real = (tb.seats || []).filter(function (s) { return s.profile_id != null && !s.is_bye; });
                var N = real.length;
                real.forEach(function (s) {
                    var e = ensure(s.profile_id);
                    if (s.placement != null) {            // doar mesele raportate
                        e.tables += 1;
                        if (mode === 'raw') {
                            e.points += (s.raw_score != null ? Number(s.raw_score) || 0 : 0);
                        } else {
                            e.points += Math.max(0, N - s.placement + 1); // plasament -> puncte
                        }
                        if (s.placement === 1) e.wins += 1;
                    }
                });
            });
        });
        // include și participanții încă fără rezultat (0 peste tot)
        (data.participants || []).forEach(function (p) {
            if (p.status === 'registered' || p.status === 'checked_in') ensure(p.profile_id);
        });

        var arr = Object.keys(byPid).map(function (k) { return byPid[k]; });
        /* sortare stabilă. Uniform: puncte desc (plasament -> puncte, mai mult = mai bine).
           Raw la 'low': sumă crescătoare (scor total mic = mai bine). Altfel desc. */
        var lowRaw = (mode === 'raw' && _dirOfData(data) === 'low');
        arr.sort(function (a, b) {
            if (b.points !== a.points) return lowRaw ? (a.points - b.points) : (b.points - a.points);
            var na = _standName(a.profile_id), nb = _standName(b.profile_id);
            return na.localeCompare(nb);
        });
        // rang cu egalități (competition ranking) + marcaj de egalitate
        var rank = 0, seen = 0, prev = null;
        arr.forEach(function (e) {
            seen++;
            if (prev === null || e.points !== prev) rank = seen;
            e.rank = rank; prev = e.points;
        });
        var counts = {};
        arr.forEach(function (e) { counts[e.points] = (counts[e.points] || 0) + 1; });
        arr.forEach(function (e) { e.tied = counts[e.points] > 1; });
        return arr;
    }

    function _standName(pid) {
        var n = (typeof displayNameById === 'function') ? displayNameById(pid) : '';
        return n || String(pid).slice(0, 8);
    }

    /* ============================================================
       NUME CLICABILE + AVATAR (aplicat peste tot în modul)
       _toAvatarFor(id)  -> mic avatar (poză/emoji/inițială) 22px
       _toPlayerChip(id) -> avatar + nume clicabil -> deschide profilul
       ============================================================ */
    function _toAvatarFor(id, size) {
        size = size || 22;
        var m = (typeof state !== 'undefined' && state.membersById) ? state.membersById[id] : null;
        var av = m && m.avatar;
        var nm = _standName(id);
        var initial = (nm || '?').trim().charAt(0).toUpperCase();
        var base = 'width:' + size + 'px;height:' + size + 'px;border-radius:50%;flex:0 0 auto;';
        if (typeof isPhotoAvatar === 'function' && isPhotoAvatar(av)) {
            return '<img src="' + _attr(av) + '" style="' + base + 'object-fit:cover;">';
        }
        if (av) {
            return '<span style="' + base + 'display:inline-flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.06);font-size:' + Math.round(size * 0.6) + 'px;">' + _esc(av) + '</span>';
        }
        return '<span style="' + base + 'display:inline-flex;align-items:center;justify-content:center;background:var(--accent-primary,#ff6b35);color:#fff;font-weight:700;font-size:' + Math.round(size * 0.5) + 'px;">' + _esc(initial) + '</span>';
    }

    /* deschide profilul jucătorului (cu statistici) — pe id, robust. */
    function _toOpenProfile(id, name) {
        try {
            if (!id) return;
            if (typeof openPublicProfileById === 'function') { openPublicProfileById(id, name || _standName(id)); return; }
            if (typeof openPlayerProfile === 'function') { openPlayerProfile(name || _standName(id)); return; }
        } catch (e) { _tlog('_toOpenProfile', e); }
    }

    /* chip jucător: [avatar] nume — clicabil, deschide profilul.
       opts: {noAvatar, suffix, strong, strike, size} */
    function _toPlayerChip(id, opts) {
        opts = opts || {};
        var name = opts.name || _standName(id);
        var av = opts.noAvatar ? '' : _toAvatarFor(id, opts.size);
        var nameStyle = 'overflow-wrap:anywhere;' + (opts.strong ? 'font-weight:700;' : '') + (opts.strike ? 'opacity:0.6;text-decoration:line-through;' : '') + (opts.nameStyle || '');
        var inner = (av ? av + ' ' : '') + '<span style="' + nameStyle + '">' + _esc(name) + '</span>' + (opts.suffix || '');
        var canOpen = id && (typeof openPublicProfileById === 'function' || typeof openPlayerProfile === 'function');
        if (!canOpen) {
            return '<span style="display:inline-flex;align-items:center;gap:6px;min-width:0;">' + inner + '</span>';
        }
        return '<span role="button" tabindex="0" title="' + _attr(_tt('tourn.viewProfile', 'View profile')) + '" ' +
            'onclick="event.stopPropagation(); _toOpenProfile(\'' + _attr(String(id)) + '\',\'' + _attr(name) + '\')" ' +
            'style="display:inline-flex;align-items:center;gap:6px;min-width:0;cursor:pointer;">' + inner + '</span>';
    }

    /* HTML clasament: comutator uniform/raw (pattern .pf-scope-btn) + tabel + share. */
    function _standingsHtml(data, tournamentId) {
        var mode = (_toStandView === 'raw') ? 'raw' : 'uniform';
        var rows = _computeStandings(data, mode);
        var myId = (typeof state !== 'undefined' && state.currentUser) ? state.currentUser.id : null;

        var toggle =
            '<div class="pf-scope-toggle" role="group" style="margin-bottom:10px;">' +
              '<button type="button" class="pf-scope-btn' + (mode === 'uniform' ? ' active' : '') + '" aria-pressed="' + (mode === 'uniform') + '" onclick="setToStandView(\'uniform\')"><i class="fas fa-ranking-star"></i> ' + _esc(_tt('tourn.stand.points', 'Tournament points')) + '</button>' +
              '<button type="button" class="pf-scope-btn' + (mode === 'raw' ? ' active' : '') + '" aria-pressed="' + (mode === 'raw') + '" onclick="setToStandView(\'raw\')"><i class="fas fa-hashtag"></i> ' + _esc(_tt('tourn.stand.raw', 'Raw score')) + '</button>' +
            '</div>';

        var head = '<div style="display:grid; grid-template-columns:32px 1fr auto auto auto; gap:8px; font-size:0.72em; opacity:0.6; padding:4px 6px; text-transform:uppercase; letter-spacing:0.03em;">' +
            '<div>' + _esc(_tt('tourn.stand.rank', '#')) + '</div>' +
            '<div>' + _esc(_tt('tourn.stand.player', 'Player')) + '</div>' +
            '<div style="text-align:right;">' + _esc(_tt('tourn.stand.pts', 'Pts')) + '</div>' +
            '<div style="text-align:right;">' + _esc(_tt('tourn.stand.wins', 'W')) + '</div>' +
            '<div style="text-align:right;">' + _esc(_tt('tourn.stand.played', 'Tables')) + '</div></div>';

        var bodyRows = rows.map(function (e) {
            var me = (String(e.profile_id) === String(myId));
            var tie = e.tied ? ' <span title="' + _attr(_tt('tourn.stand.tie', 'tie')) + '" style="opacity:0.6;">=</span>' : '';
            return '<div style="display:grid; grid-template-columns:32px 1fr auto auto auto; gap:8px; padding:6px; border-bottom:1px solid var(--border,rgba(255,255,255,0.05));' + (me ? ' background:rgba(255,107,53,0.06);' : '') + '">' +
                '<div style="opacity:0.7;">' + e.rank + tie + '</div>' +
                '<div style="min-width:0;">' + _toPlayerChip(e.profile_id, { strong: me, nameStyle: me ? 'color:var(--accent-primary,#ff6b35);' : '' }) + '</div>' +
                '<div style="text-align:right; font-weight:700;">' + (Math.round(e.points * 100) / 100) + '</div>' +
                '<div style="text-align:right; opacity:0.8;">' + e.wins + '</div>' +
                '<div style="text-align:right; opacity:0.8;">' + e.tables + '</div></div>';
        }).join('');

        return '<div style="margin-top:18px; padding-top:12px; border-top:1px solid var(--border,#333);">' +
            '<div style="font-weight:700; margin-bottom:8px;"><i class="fas fa-ranking-star"></i> ' + _esc(_tt('tourn.standings', 'Standings')) + '</div>' +
            toggle + head + (bodyRows || '<div style="opacity:0.6; padding:8px;">' + _esc(_tt('tourn.noResults', 'No results yet.')) + '</div>') + '</div>';
    }

    /* comutator vizualizare clasament — re-randează pagina. */
    function setToStandView(v) {
        _toStandView = (v === 'raw') ? 'raw' : 'uniform';
        if (_toCurrentPageId) _renderTournamentPageBody(_toCurrentPageId);
    }

    /* ============================================================
       STATISTICI DE TURNEU ÎN PROFIL — jucate / câștigate / podium.
       Eficient: 3 interogări (participări -> turnee finalizate -> locuri în bloc),
       fără N interogări per turneu. Podium = loc final 1-3 în clasament.
       ============================================================ */
    async function injectTournamentStats(containerId, profileId) {
        var el = document.getElementById(containerId);
        if (!el || !_sbReady() || !profileId) return;
        var clubId = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        if (!clubId) return;
        try {
            // 1) participările jucătorului în acest club
            var partRes = await sb.from('tournament_participants')
                .select('tournament_id, status')
                .eq('club_id', clubId).eq('profile_id', profileId);
            if (partRes.error) { _tlog('injectTournamentStats.part', partRes.error); return; }
            var partRows = partRes.data || [];
            if (!partRows.length) { el.innerHTML = ''; return; }
            var tIds = partRows.map(function (r) { return r.tournament_id; });

            // 2) turneele FINALIZATE dintre acestea (+ winner_id)
            var tRes = await sb.from('tournaments')
                .select('id, winner_id, status')
                .eq('club_id', clubId).in('id', tIds).eq('status', 'completed');
            if (tRes.error) { _tlog('injectTournamentStats.tourn', tRes.error); return; }
            var completed = tRes.data || [];
            if (!completed.length) { el.innerHTML = ''; return; }
            var completedIds = completed.map(function (t) { return t.id; });
            var winnerByT = {}; completed.forEach(function (t) { winnerByT[t.id] = t.winner_id; });

            // 3) TOATE locurile pentru turneele finalizate (o interogare în bloc)
            var seatsRes = await sb.from('tournament_seats')
                .select('tournament_id, table_id, profile_id, placement, is_bye')
                .eq('club_id', clubId).in('tournament_id', completedIds);
            if (seatsRes.error) { _tlog('injectTournamentStats.seats', seatsRes.error); return; }
            var seats = seatsRes.data || [];

            // grupăm locurile pe turneu -> calculăm clasamentul final -> rangul jucătorului
            var byTourn = {};
            seats.forEach(function (s) {
                (byTourn[s.tournament_id] = byTourn[s.tournament_id] || []).push(s);
            });

            var played = completed.length, won = 0, podium = 0;
            completed.forEach(function (t) {
                var rankMap = _rankFromFlatSeats(byTourn[t.id] || []);
                var myRank = rankMap[profileId];
                if (winnerByT[t.id] && String(winnerByT[t.id]) === String(profileId)) won++;
                if (myRank != null && myRank <= 3) podium++;
            });

            var card = function (label, value, icon) {
                return '<div class="stat-card"><div class="stat-label"><i class="fas ' + icon + '"></i> ' + _esc(label) + '</div><div class="stat-value">' + value + '</div></div>';
            };
            el.innerHTML =
                '<h3 style="margin:18px 0 10px;"><i class="fas fa-trophy"></i> ' + _esc(_tt('tourn.stats.title', 'Tournaments')) + '</h3>' +
                '<div class="stats-grid">' +
                  card(_tt('tourn.stats.played', 'Played'), played, 'fa-flag-checkered') +
                  card(_tt('tourn.stats.won', 'Won'), won, 'fa-crown') +
                  card(_tt('tourn.stats.podium', 'Podiums'), podium, 'fa-medal') +
                '</div>';
        } catch (e) { _tlog('injectTournamentStats', e); }
    }

    /* Clasament final din locuri „plate" (toate seat-urile unui turneu):
       puncte după plasament la masă (N - loc + 1), rang cu egalități. */
    function _rankFromFlatSeats(seats) {
        var byTable = {};
        seats.forEach(function (s) {
            if (s.is_bye || s.profile_id == null) return;
            (byTable[s.table_id] = byTable[s.table_id] || []).push(s);
        });
        var pts = {};
        Object.keys(byTable).forEach(function (tid) {
            var grp = byTable[tid];
            var N = grp.length;
            grp.forEach(function (s) {
                if (s.placement == null) return;
                pts[s.profile_id] = (pts[s.profile_id] || 0) + Math.max(0, N - s.placement + 1);
            });
        });
        var arr = Object.keys(pts).map(function (pid) { return { pid: pid, p: pts[pid] }; });
        arr.sort(function (a, b) { return b.p - a.p; });
        var rankMap = {}, rank = 0, seen = 0, prev = null;
        arr.forEach(function (e) {
            seen++;
            if (prev === null || e.p !== prev) rank = seen;
            rankMap[e.pid] = rank; prev = e.p;
        });
        return rankMap;
    }

    /* Admin = rank >= 3 (secondary_admin sau principal_admin). */
    function _toIsAdmin() {
        var r = (typeof state !== 'undefined' && state.currentUser) ? (state.currentUser.clubRoleRank || 0) : 0;
        return r >= 3;
    }

    /* ---- Dialog de finalizare: câștigător (din clasament, cu tie-break uman) + bonus Admin ---- */
    async function _toOpenFinalize() {
        var id = _toCurrentPageId;
        if (!id) return;
        var data = await toLoadTournament(id);
        if (!data || !data.tournament) return;
        var t = data.tournament;

        // clasament final (mod uniform = plasament) pentru a determina liderul
        var standings = _computeStandings(data, 'uniform').filter(function (e) { return e.tables > 0; });
        var leaders = [];
        if (standings.length) {
            var top = standings[0].points;
            leaders = standings.filter(function (e) { return e.points === top; });
        }

        // avertizare: mese neraportate
        var totalTables = 0, reported = 0;
        (data.rounds || []).forEach(function (rd) {
            (rd.tables || []).forEach(function (tb) {
                var real = (tb.seats || []).filter(function (s) { return s.profile_id != null && !s.is_bye; });
                if (!real.length) return;
                totalTables++;
                if (tb.status === 'reported' || tb.status === 'confirmed') reported++;
            });
        });
        var pending = totalTables - reported;

        // stare selecție câștigător (default: liderul unic, dacă e)
        _toFinalizeWinner = (leaders.length === 1) ? leaders[0].profile_id : null;

        var modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.toFinalize = String(id);
        modal.style.zIndex = '2147483601';

        var warnHtml = pending > 0
            ? '<div style="margin:8px 0; padding:10px; border-radius:8px; border:1px solid var(--warning,#f0ad4e); background:rgba(240,173,78,0.08); font-size:0.85em;"><i class="fas fa-triangle-exclamation"></i> ' +
              _esc(_tt('tourn.finalizeWarnPending', 'Some tables are not reported yet. They will count as 0 points.')) + ' (' + pending + ')</div>'
            : '';

        // secțiune câștigător: dacă egalitate pe loc 1 -> alegere manuală (tie-break uman)
        var winnerHtml;
        if (leaders.length <= 1) {
            var wname = _toFinalizeWinner ? _standName(_toFinalizeWinner) : _tt('tourn.noWinner', 'No winner');
            winnerHtml = '<div style="margin:8px 0;"><div style="font-size:0.72em; opacity:0.7; text-transform:uppercase;">' + _esc(_tt('tourn.champion', 'Champion')) + '</div>' +
                '<div style="font-weight:700; font-size:1.05em; margin-top:2px;"><i class="fas fa-trophy" style="color:var(--accent-gold,#ffd700);"></i> ' + _esc(wname) + '</div></div>';
        } else {
            winnerHtml = '<div style="margin:8px 0; padding:10px; border-radius:8px; border:1px solid var(--accent-gold,#ffd700); background:rgba(255,215,0,0.06);">' +
                '<div style="font-weight:700; display:flex; align-items:center; gap:8px;"><i class="fas fa-khanda" style="color:var(--accent-gold,#ffd700);"></i> ' + _esc(_tt('tourn.tieForFirst', 'Tie for 1st — pick the champion per the game rules')) + '</div>' +
                '<div style="display:flex; flex-wrap:wrap; gap:8px; margin-top:8px;" class="to-final-leaders">' +
                leaders.map(function (e) {
                    return '<button type="button" class="btn btn-sm btn-secondary" data-pid="' + _attr(e.profile_id) + '" onclick="_toPickWinner(\'' + _attr(e.profile_id) + '\')" style="display:inline-flex; align-items:center; gap:6px;"><i class="fas fa-khanda"></i> ' + _toAvatarFor(e.profile_id, 20) + ' ' + _esc(_standName(e.profile_id)) + '</button>';
                }).join('') + '</div></div>';
        }

        // bonus final: doar dacă e activat pe turneu ȘI apelantul e Admin
        var bonusHtml = '';
        if (t.final_points_enabled && _toIsAdmin()) {
            var prizes = (t.final_points_template && Array.isArray(t.final_points_template.prizes))
                ? t.final_points_template.prizes.map(Number) : [10, 6, 3];
            var prizeStr = prizes.map(function (p, i) { return (i + 1) + '° ' + p; }).join(' · ');
            bonusHtml = '<label style="display:flex; align-items:center; gap:8px; margin:10px 0 4px; font-size:0.9em;">' +
                '<input type="checkbox" id="toAwardFinal" checked> ' + _esc(_tt('tourn.awardFinalPoints', 'Award final Club Points bonus (by final standings)')) + '</label>' +
                '<div style="font-size:0.8em; opacity:0.75; margin-bottom:6px;"><i class="fas fa-gift"></i> ' + _esc(prizeStr) + '</div>';
        } else if (t.final_points_enabled && !_toIsAdmin()) {
            bonusHtml = '<div style="margin:8px 0; font-size:0.82em; opacity:0.7;"><i class="fas fa-circle-info"></i> ' + _esc(_tt('tourn.bonusAdminOnly', 'The final points bonus can only be awarded by an admin.')) + '</div>';
        }

        modal.innerHTML = '<div class="modal-content" style="max-width:480px;"><div class="modal-header">' +
            '<h2><i class="fas fa-flag-checkered"></i> ' + _esc(_tt('tourn.finalizeTitle', 'Finalize tournament')) + '</h2>' +
            '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>' +
            warnHtml + winnerHtml + bonusHtml +
            '<button class="btn btn-primary" style="margin-top:12px;" onclick="_toConfirmFinalize()"><i class="fas fa-flag-checkered"></i> ' + _esc(_tt('tourn.act.finish', 'Finish')) + '</button>' +
            '</div>';
        document.body.appendChild(modal);
    }

    var _toFinalizeWinner = null;
    /* alegerea manuală a campionului la egalitate pe loc 1 (tie-break uman). */
    function _toPickWinner(pid) {
        _toFinalizeWinner = pid;
        var modal = document.querySelector('.modal.active[data-to-finalize]');
        if (!modal) return;
        Array.prototype.forEach.call(modal.querySelectorAll('.to-final-leaders button'), function (b) {
            var on = b.getAttribute('data-pid') === pid;
            b.className = 'btn btn-sm ' + (on ? 'btn-primary' : 'btn-secondary');
            var ic = b.querySelector('i'); if (ic) ic.className = 'fas fa-' + (on ? 'trophy' : 'khanda');
        });
    }

    async function _toConfirmFinalize() {
        var id = _toCurrentPageId;
        if (!id) return;
        var modal = document.querySelector('.modal.active[data-to-finalize]');
        var awardEl = modal ? modal.querySelector('#toAwardFinal') : null;
        var award = !!(awardEl && awardEl.checked);

        var finalPoints = null;
        if (award) {
            // bonus = PREMIUL pentru locul FINAL, din final_points_template.prizes
            // (locul 1 -> prizes[0], ... ; locurile din afara listei iau 0)
            var data = _toPageData && String(_toPageData.tournament.id) === String(id) ? _toPageData : await toLoadTournament(id);
            var prizes = (data.tournament.final_points_template && Array.isArray(data.tournament.final_points_template.prizes))
                ? data.tournament.final_points_template.prizes.map(Number) : [10, 6, 3];
            var standings = _computeStandings(data, 'uniform').filter(function (e) { return e.tables > 0; });
            finalPoints = {};
            standings.forEach(function (e) {
                var p = (e.rank >= 1 && e.rank <= prizes.length) ? Number(prizes[e.rank - 1]) : 0;
                if (p) finalPoints[e.profile_id] = p;   // trimitem doar premii ne-nule
            });
            if (!Object.keys(finalPoints).length) finalPoints = null;
        }

        var res = await toFinalize(id, _toFinalizeWinner, finalPoints);
        if (!res) return;
        if (modal) modal.remove();
        // dacă s-au acordat puncte bonus, reîncarcă starea clubului (leaderboard/puncte membri) imediat
        if (res.final_points_awarded && typeof hydrateFromSupabase === 'function') {
            try { await hydrateFromSupabase(); } catch (e) { _tlog('finalize.hydrate', e); }
            try { if (typeof renderLeaderboard === 'function') renderLeaderboard(); } catch (e) {}
        }
        await _renderTournamentPageBody(id);
        // notificare: campion desemnat -> tot clubul
        try {
            var fd = _toPageData || await toLoadTournament(id);
            var champName = _toFinalizeWinner ? _standName(_toFinalizeWinner) : '';
            var nmF = (fd && fd.tournament && fd.tournament.name) || '';
            var membersF = await _toActiveMemberIds(_clubId());
            var txt = _tt('tourn.notif.finalized', '{name} — champion: {champion}')
                .replace('{name}', nmF).replace('{champion}', champName);
            _toNotify(membersF, id, txt, 'fa-trophy');
        } catch (e) { _tlog('_toConfirmFinalize.notif', e); }
        if (typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
    }

    async function _toReopenFromPage() {
        var id = _toCurrentPageId;
        if (!id) return;
        var ok = (typeof confirmModal === 'function')
            ? await confirmModal(_tt('tourn.reopenConfirm', 'Reopen this tournament? The final points bonus (if any) will be reversed.'), { title: _tt('tourn.act.reopen', 'Reopen') })
            : true;
        if (!ok) return;
        var res = await toReopen(id);
        if (!res) return;
        // redeschiderea reversează bonusul -> reîncarcă punctele dacă s-a schimbat ceva
        if (res.reversed && Object.keys(res.reversed).length && typeof hydrateFromSupabase === 'function') {
            try { await hydrateFromSupabase(); } catch (e) { _tlog('reopen.hydrate', e); }
            try { if (typeof renderLeaderboard === 'function') renderLeaderboard(); } catch (e) {}
        }
        await _renderTournamentPageBody(id);
        if (typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
    }

    /* ---- Share turneu (delegat din buildShareData, kind='tournament') ----
       Sincron: citește din _toPageData (cache-ul paginii deschise). Rezumat + clasament. */
    function buildTournamentShareData(id) {
        var data = (_toPageData && _toPageData.tournament && String(_toPageData.tournament.id) === String(id))
            ? _toPageData : null;
        if (!data) return null;
        var t = data.tournament;
        var lines = [];
        lines.push({ text: '🏆 ' + _tt('tourn.status.' + t.status, t.status), strong: true });
        if (t.game_title) lines.push({ text: '🎲 ' + t.game_title, muted: true });
        lines.push({ text: '🗂 ' + toFormatLabel(t.format), muted: true });
        var reg = (data.participants || []).filter(function (p) { return p.status === 'registered' || p.status === 'checked_in'; }).length;
        lines.push({ text: '👥 ' + _tt('tourn.participants', 'Participants') + ': ' + reg, muted: true });
        if (t.prizes && t.prizes.text) lines.push({ text: '🎁 ' + t.prizes.text, muted: true });

        var mode = (_toStandView === 'raw') ? 'raw' : 'uniform';
        var rows = _computeStandings(data, mode).filter(function (e) { return e.tables > 0; }).slice(0, 8);
        if (rows.length) {
            lines.push({ text: '— ' + _tt('tourn.standings', 'Standings') + ' —', strong: true });
            rows.forEach(function (e) {
                var pts = (Math.round(e.points * 100) / 100);
                lines.push({ text: e.rank + (e.tied ? '=' : '.') + ' ' + _standName(e.profile_id) + ' · ' + pts + ' ' + _tt('tourn.stand.pts', 'pts') });
            });
        }
        return { title: t.name, lines: lines };
    }

    /* Generează bracket-ul RR din pagina de turneu (Moderator+). */
    async function _toGenerateBracketFromPage() {
        var id = _toCurrentPageId;
        if (!id) return;
        var data = await toLoadTournament(id);
        if (!data || !data.tournament) return;
        var t = data.tournament;

        if (t.format !== 'round_robin' && t.format !== 'single_elim' && t.format !== 'tables' && t.format !== 'swiss') {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.formatNoAutogen', 'This format is generated round by round.'), 'error');
            return;
        }

        var active = (data.participants || []).filter(function (p) { return p.status === 'registered' || p.status === 'checked_in'; });
        var ids = active.map(function (p) { return p.profile_id; });
        if (ids.length < 2) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.NOT_ENOUGH_PARTICIPANTS', 'Not enough participants.'), 'error');
            return;
        }

        // confirmare la regenerare (există deja runde)
        if ((data.rounds || []).length) {
            var ok = (typeof confirmModal === 'function')
                ? await confirmModal(_tt('tourn.confirmRegen', 'This will replace the existing bracket. Continue?'), { danger: true, title: _tt('tourn.regenBracket', 'Regenerate bracket') })
                : true;
            if (!ok) return;
        }

        var pairing;
        if (t.format === 'single_elim') {
            // runda 1: împerechere după toggle (random/seed). „seed” = ordinea de înscriere.
            var seeded = ids.slice();
            if (!(t.settings && t.settings.seeding === 'seed')) seeded = _shuffle(seeded, Math.random);
            var seatsE = Math.max(2, Math.min(12, (t.seats_per_table | 0) || 4));
            var round1 = generateElimRound(seeded, seatsE, 1);
            pairing = round1 ? [round1] : [];
        } else if (t.format === 'swiss') {
            // runda 1 Swiss: random/seed, mese de N (fără istoric de reîntâlniri încă)
            var seededS = ids.slice();
            if (!(t.settings && t.settings.seeding === 'seed')) seededS = _shuffle(seededS, Math.random);
            var seatsS = Math.max(2, Math.min(12, (t.seats_per_table | 0) || 4));
            var sRound1 = generateSwissRound(seededS, seatsS, 1, {});
            pairing = sRound1 ? [sRound1] : [];
        } else {
            // round_robin ȘI tables: mese de N (seats configurabil). seats=2 -> 1v1 clasic cu tur/retur.
            var legs = (t.settings && t.settings.legs === 2) ? 2 : 1;
            var seatsR = Math.max(2, Math.min(12, (t.seats_per_table | 0) || 2));
            pairing = generateRoundRobinTables(ids, seatsR, { legs: legs });
        }
        if (!pairing.length) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.EMPTY_PAIRING', 'Empty pairing.'), 'error');
            return;
        }

        var res = await toApplyPairing(id, pairing, ids);
        if (res) {
            await _renderTournamentPageBody(id);
            // notificare: prima rundă / bracket gata -> participanții
            try {
                var pids = ids.filter(function (x) { return x !== _toActorId(); });
                _toNotify(pids, id, _toNotifText('tourn.notif.round', 'New round ready: {name}', t.name), 'fa-forward-step');
            } catch (e) { _tlog('_toGenerateBracket.notif', e); }
        }
    }

    /* Single-elim: generează runda următoare (avansare atomică server-side). */
    async function _toAdvanceElimFromPage() {
        var id = _toCurrentPageId;
        if (!id) return;
        var res = await toAdvanceElim(id);
        if (!res) return;
        if (res.done) {
            _toToastOk('tourn.elimReadyFinal', 'Final round played — ready to finalize.');
        } else {
            _toToastOk('tourn.elimNextRound', 'Next round generated.');
            try {
                var d = await toLoadTournament(id);
                _toNotify(_toParticipantIds(d), id, _toNotifText('tourn.notif.round', 'New round ready: {name}', d && d.tournament && d.tournament.name), 'fa-forward-step');
            } catch (e) { _tlog('_toAdvanceElim.notif', e); }
        }
        await _renderTournamentPageBody(id);
    }

    /* Swiss: generează runda următoare. Verifică starea server-side, apoi
       împerechează după clasament (evitând reîntâlnirile) și scrie prin apply_pairing. */
    async function _toAdvanceSwissFromPage() {
        var id = _toCurrentPageId;
        if (!id) return;
        // 1) stare server-side (garanția „rundă completă” + done)
        var stateInfo = await toSwissState(id);
        if (!stateInfo) return;
        if (!stateInfo.current_complete) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.ROUND_NOT_COMPLETE', 'Not all tables in the current round are reported.'), 'error');
            return;
        }
        if (stateInfo.done) {
            _toToastOk('tourn.swissReadyFinal', 'All rounds played — ready to finalize.');
            return;
        }

        // 2) date curente: clasament + istoric reîntâlniri
        var data = await toLoadTournament(id);
        if (!data || !data.tournament) return;
        var t = data.tournament;

        // clasament curent (mod uniform = plasament), doar activi
        var standings = _computeStandings(data, 'uniform');
        var activeSet = {};
        (data.participants || []).forEach(function (p) {
            if (p.status === 'registered' || p.status === 'checked_in') activeSet[p.profile_id] = true;
        });
        var rankedIds = standings
            .filter(function (e) { return activeSet[e.profile_id]; })
            .map(function (e) { return e.profile_id; });
        // includem și eventualii activi fără rezultat (la coada clasamentului)
        Object.keys(activeSet).forEach(function (pid) {
            if (rankedIds.indexOf(pid) === -1) rankedIds.push(pid);
        });
        if (rankedIds.length < 2) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.NOT_ENOUGH_PARTICIPANTS', 'Not enough participants.'), 'error');
            return;
        }

        // istoricul de reîntâlniri din toate rundele existente
        var past = {};
        (data.rounds || []).forEach(function (rd) {
            var p = _pairsFromRound(rd.tables || []);
            Object.keys(p).forEach(function (k) { past[k] = true; });
        });

        // 3) împerechere Swiss
        var seats = Math.max(2, Math.min(12, (t.seats_per_table | 0) || 4));
        var nextNo = (stateInfo.last_round || 0) + 1;
        var round = generateSwissRound(rankedIds, seats, nextNo, past);
        if (!round) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.EMPTY_PAIRING', 'Empty pairing.'), 'error');
            return;
        }

        // 4) scriere prin apply_pairing (adaugă runda nouă, nu atinge cele vechi)
        var res = await toApplyPairing(id, [round], rankedIds);
        if (!res) return;
        _toToastOk('tourn.swissNextRound', 'Next round generated.');
        await _renderTournamentPageBody(id);
        try {
            var pidsS = rankedIds.filter(function (x) { return x !== _toActorId(); });
            _toNotify(pidsS, id, _toNotifText('tourn.notif.round', 'New round ready: {name}', t.name), 'fa-forward-step');
        } catch (e) { _tlog('_toAdvanceSwiss.notif', e); }
    }

    /* acțiuni din pagina de turneu (refresh body la succes) */
    async function _toJoinFromPage() {
        if (!_toCurrentPageId) return;
        var res = await toJoinTournament(_toCurrentPageId);
        if (res) await _renderTournamentPageBody(_toCurrentPageId);
    }
    async function _toLeaveFromPage() {
        if (!_toCurrentPageId) return;
        var res = await toLeaveTournament(_toCurrentPageId);
        if (res) await _renderTournamentPageBody(_toCurrentPageId);
    }
    async function _toSetStatusFromPage(status) {
        if (!_toCurrentPageId) return;
        var id = _toCurrentPageId;
        var res = await toSetTournamentStatus(id, status);
        if (res) {
            await _renderTournamentPageBody(id);
            if (typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
            // notificări: înscriere deschisă -> toți membrii; live -> participanții
            try {
                var data = _toPageData || await toLoadTournament(id);
                var nm = (data && data.tournament && data.tournament.name) || '';
                if (status === 'registration') {
                    var members = await _toActiveMemberIds(_clubId());
                    _toNotify(members, id, _toNotifText('tourn.notif.registration', 'Registration open: {name}', nm), 'fa-clipboard-list');
                } else if (status === 'live') {
                    _toNotify(_toParticipantIds(data), id, _toNotifText('tourn.notif.live', 'Tournament started: {name}', nm), 'fa-play');
                }
            } catch (e) { _tlog('_toSetStatusFromPage.notif', e); }
        }
    }

    /* ============================================================
       UI — CLASAMENT ANUAL / CAMPION DE CLUB
       Modal cu selector de an + clasament cumulat. Editor de scală (Admin).
       ============================================================ */
    var _toSeriesYear = (new Date()).getFullYear();
    var _toSeriesScale = [10, 6, 4, 3, 2, 1];
    var _toSeriesView = 'series';           // 'series' (puncte de serie) | 'raw' (scor brut)

    async function openSeriesStandings() {
        if (typeof requireFeature === 'function' && !requireFeature('tournaments')) return;
        var stale = document.querySelectorAll('.modal.active[data-series-page]');
        for (var i = 0; i < stale.length; i++) stale[i].remove();

        var modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.seriesPage = '1';
        modal.innerHTML = '<div class="modal-content"><div class="modal-header">' +
            '<h2><i class="fas fa-medal"></i> ' + _esc(_tt('tourn.series.title', 'Club champion (annual)')) + '</h2>' +
            '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>' +
            '<div class="to-series-body"><div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' +
            _esc(_tt('tourn.loading', 'Loading…')) + '</div></div></div>';
        document.body.appendChild(modal);
        await _renderSeriesBody();
    }

    /* cache datele serie pe an, ca toggle-ul serie/brut să nu re-ceară de la server. */
    var _toSeriesCache = { year: null, data: null };

    /* re-desenare completă: fetch de la server (an nou / scală schimbată), apoi paint. */
    async function _renderSeriesBody() {
        var modal = document.querySelector('.modal.active[data-series-page]');
        if (!modal) return;
        var body = modal.querySelector('.to-series-body');
        if (!body) return;
        body.innerHTML = '<div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' + _esc(_tt('tourn.loading', 'Loading…')) + '</div>';

        var data = await toSeriesStandings(_toSeriesYear);
        if (!data) { body.innerHTML = '<div style="padding:20px; color:var(--danger,#e74c3c);">' + _esc(_tt('tourn.err.generic', 'Something went wrong.')) + '</div>'; return; }
        _toSeriesCache = { year: _toSeriesYear, data: data };
        try { var sc = data.scale; if (Array.isArray(sc) && sc.length) _toSeriesScale = sc.map(Number); } catch (e) {}
        _paintSeries();
    }

    /* desenare din cache (toggle serie/brut) — instant, fără loading/flash. */
    function _paintSeries() {
        var modal = document.querySelector('.modal.active[data-series-page]');
        if (!modal) return;
        var body = modal.querySelector('.to-series-body');
        if (!body || !_toSeriesCache.data) return;

        var data = _toSeriesCache.data;
        var standings = data.standings || [];
        var scale = data.scale || [];
        var isAdmin = _toIsAdmin();

        var nowY = (new Date()).getFullYear();
        var yearOpts = '';
        for (var y = nowY; y >= nowY - 4; y--) {
            yearOpts += '<option value="' + y + '"' + (y === _toSeriesYear ? ' selected' : '') + '>' + y + '</option>';
        }

        function nameOf(pid) {
            var n = (typeof displayNameById === 'function') ? displayNameById(pid) : '';
            return n || String(pid).slice(0, 8);
        }

        var view = (_toSeriesView === 'raw') ? 'raw' : 'series';
        var metric = function (e) { return view === 'raw' ? Number(e.raw_points || 0) : Number(e.series_points || 0); };

        var header = '<div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:12px;">' +
            '<span class="form-section-label" style="font-size:0.85em; opacity:0.75;">' + _esc(_tt('tourn.series.year', 'Year')) + '</span>' +
            '<select class="form-control to-series-year" style="width:auto;" onchange="_toSeriesSetYear(this.value)">' + yearOpts + '</select>' +
            '<div style="flex:1;"></div>' +
            (isAdmin ? '<button class="btn btn-sm" onclick="_toOpenScaleEditor()"><i class="fas fa-sliders"></i> ' + _esc(_tt('tourn.series.editScale', 'Edit scale')) + '</button>' : '') +
            '</div>';

        var toggle = '<div class="pf-scope-toggle" role="group" style="margin-bottom:10px;">' +
            '<button type="button" class="pf-scope-btn' + (view === 'series' ? ' active' : '') + '" aria-pressed="' + (view === 'series') + '" onclick="_toSetSeriesView(\'series\')"><i class="fas fa-ranking-star"></i> ' + _esc(_tt('tourn.series.viewSeries', 'Series points')) + '</button>' +
            '<button type="button" class="pf-scope-btn' + (view === 'raw' ? ' active' : '') + '" aria-pressed="' + (view === 'raw') + '" onclick="_toSetSeriesView(\'raw\')"><i class="fas fa-hashtag"></i> ' + _esc(_tt('tourn.stand.raw', 'Raw score')) + '</button>' +
            '</div>';

        var scaleLine = (view === 'series') ? ('<div style="font-size:0.78em; opacity:0.65; margin-bottom:10px;">' +
            _esc(_tt('tourn.series.scaleLabel', 'Points per place:')) + ' ' + _esc(scale.map(function (v, i) { return (i + 1) + '°=' + v; }).join(' · ')) + '</div>') : '';

        if (!standings.length) {
            body.innerHTML = header + toggle +
                '<div style="padding:20px; text-align:center; color:var(--text-secondary,#bbb);">' +
                _esc(_tt('tourn.series.empty', 'No series tournaments completed this year yet.')) + '</div>';
            return;
        }

        var sorted = standings.slice().sort(function (a, b) { return metric(b) - metric(a); });
        var dispRank = [], rr = 0, prev = null;
        sorted.forEach(function (e, i) { var m = metric(e); if (prev === null || m !== prev) rr = i + 1; dispRank.push(rr); prev = m; });

        var ptsLabel = (view === 'raw') ? _tt('tourn.stand.raw', 'Raw') : _tt('tourn.series.points', 'Pts');
        // grid: rang | nume(flex, minmax 0) | pct | J | C | P — minmax(0,1fr) forțează încadrarea + trunchierea
        var GRID = 'display:grid; grid-template-columns:22px minmax(0,1fr) 44px 30px 30px 34px; gap:6px; align-items:center;';

        var head = '<div style="' + GRID + ' padding:4px 2px; opacity:0.55; font-size:0.72em; text-transform:uppercase; letter-spacing:0.03em;">' +
            '<div>#</div><div>' + _esc(_tt('tourn.series.player', 'Player')) + '</div>' +
            '<div style="text-align:right;">' + _esc(ptsLabel) + '</div>' +
            '<div style="text-align:center;" title="' + _attr(_tt('tourn.stats.played', 'Played')) + '">' + _esc(_tt('tourn.abbr.played', 'P')) + '</div>' +
            '<div style="text-align:center;" title="' + _attr(_tt('tourn.stats.won', 'Won')) + '">' + _esc(_tt('tourn.abbr.won', 'W')) + '</div>' +
            '<div style="text-align:center;" title="' + _attr(_tt('tourn.stats.podium', 'Podiums')) + '">' + _esc(_tt('tourn.abbr.podium', '🏅')) + '</div>' +
            '</div>';

        var rows = sorted.map(function (e, i) {
            var rank = dispRank[i];
            var champ = (rank === 1);
            var medal = champ ? '🏆 ' : (rank === 2 ? '🥈 ' : (rank === 3 ? '🥉 ' : ''));
            return '<div style="' + GRID + ' padding:6px 2px; border-top:1px solid var(--border,rgba(255,255,255,0.06));' + (champ ? ' font-weight:700;' : '') + '">' +
                '<div style="opacity:0.7;">' + rank + '</div>' +
                '<div style="text-align:left; min-width:0; display:flex; align-items:center; gap:5px; cursor:pointer;" title="' + _attr(_tt('tourn.viewProfile', 'View profile')) + '" onclick="event.stopPropagation(); _toOpenProfile(\'' + _attr(String(e.profile_id)) + '\',\'' + _attr(nameOf(e.profile_id)) + '\')">' +
                    (medal ? '<span style="flex:0 0 auto;">' + medal + '</span>' : '') + _toAvatarFor(e.profile_id, 18) +
                    '<span style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">' + _esc(nameOf(e.profile_id)) + '</span></div>' +
                '<div style="text-align:right; font-weight:700;">' + metric(e) + '</div>' +
                '<div style="text-align:center; opacity:0.8;">' + e.played + '</div>' +
                '<div style="text-align:center; opacity:0.8;">' + e.won + '</div>' +
                '<div style="text-align:center; opacity:0.8;">' + e.podiums + '</div>' +
                '</div>';
        }).join('');

        body.innerHTML = header + toggle + scaleLine + head + rows;
    }

    function _toSetSeriesView(v) {
        _toSeriesView = (v === 'raw') ? 'raw' : 'series';
        _paintSeries();               // doar re-desenare din cache — fără flash
    }

    function _toSeriesSetYear(y) {
        _toSeriesYear = parseInt(y, 10) || (new Date()).getFullYear();
        _renderSeriesBody();          // an nou -> re-fetch
    }

    /* editor de scală (Admin) — scrie club_settings key 'series_points' (ca location/reminders). */
    function _toOpenScaleEditor() {
        if (!_toIsAdmin()) return;
        var cur = _toReadClubScale();
        var editor = document.createElement('div');
        editor.className = 'modal active';
        editor.dataset.scaleEditor = '1';
        editor.style.zIndex = '2147483602';
        editor.innerHTML = '<div class="modal-content" style="max-width:420px;"><div class="modal-header">' +
            '<h2><i class="fas fa-sliders"></i> ' + _esc(_tt('tourn.series.editScale', 'Edit scale')) + '</h2>' +
            '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>' +
            '<div class="form-group"><label for="toScaleInput">' + _esc(_tt('tourn.series.scaleInput', 'Points per place (1st, 2nd, 3rd …)')) + '</label>' +
            '<input type="text" id="toScaleInput" class="form-control" value="' + _attr(cur.join(', ')) + '" placeholder="10, 6, 4, 3, 2, 1">' +
            '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.series.scaleHint', 'Comma-separated. Places beyond the list get 0.')) + '</small></div>' +
            '<button class="btn btn-primary" onclick="_toSaveScale()"><i class="fas fa-save"></i> ' + _esc(_tt('common.save', 'Save')) + '</button>' +
            '</div>';
        document.body.appendChild(editor);
    }

    /* scala clubului: din ce a întors ultima dată RPC-ul (sursă de adevăr = server). */
    function _toReadClubScale() {
        return (Array.isArray(_toSeriesScale) && _toSeriesScale.length) ? _toSeriesScale.slice() : [10, 6, 4, 3, 2, 1];
    }

    async function _toSaveScale() {
        if (!_toIsAdmin() || !_sbReady()) return;
        var cid = _clubId();
        if (!cid) return;
        var raw = (document.getElementById('toScaleInput') || {}).value || '';
        var arr = raw.split(',').map(function (x) { return Number(String(x).trim()); }).filter(function (n) { return !isNaN(n) && n >= 0; });
        if (!arr.length) { if (typeof showNotification === 'function') showNotification(_tt('tourn.series.scaleBad', 'Enter at least one number.'), 'error'); return; }
        try {
            var up = await sb.from('club_settings').upsert(
                { club_id: cid, key: 'series_points', value: arr, updated_at: new Date().toISOString() },
                { onConflict: 'club_id,key' }
            );
            if (up.error) { _toToastErr(up.error); return; }
            // reflectă în state local ca să apară imediat
            try { if (typeof state !== 'undefined') { state.settings = state.settings || {}; state.settings.series_points = arr; } } catch (e) {}
            _toToastOk('tourn.series.scaleSaved', 'Scale saved.');
            var ed = document.querySelector('.modal.active[data-scale-editor]');
            if (ed) ed.remove();
            _renderSeriesBody();
        } catch (e) { _tlog('_toSaveScale', e); _toToastErr(e); }
    }

    /* ============================================================
       UI — FORMULAR CREARE TURNEU (Batch 3c)
       Modal identic ca pattern cu openCampaignEditor (§11). Colectează
       payload-ul și apelează TO_TOURN.create() (RPC atomic, gated).
       Câmpuri condiționale pe format (mese de N, top X, legs).
       ============================================================ */
    function openTournamentEditor(existing) {
        if (typeof requireFeature === 'function' && !requireFeature('tournaments')) return;
        var isEdit = !!(existing && existing.id);
        if (!isEdit && typeof can === 'function' && !can('tournament.manage')) {
            if (typeof showNotification === 'function') showNotification(_tt('tourn.err.FORBIDDEN', 'Not allowed.'), 'error');
            return;
        }
        _toEditingId = isEdit ? existing.id : null;
        // catalog de jocuri (title -> {dbId, imageUrl, ...}) pentru picker-ul cu search + poze
        var catalog = (typeof state !== 'undefined' && state.catalog) ? state.catalog : {};

        var fmtOpts = ['round_robin', 'single_elim', 'swiss', 'tables'].map(function (f) {
            return '<option value="' + f + '">' + _esc(toFormatLabel(f)) + '</option>';
        }).join('');

        var modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.tournEditor = '1';
        modal.innerHTML =
            '<div class="modal-content"><div class="modal-header">' +
            '<h2><i class="fas fa-trophy"></i> ' + _esc(_tt('tourn.new', 'New tournament')) + '</h2>' +
            '<button class="modal-close" onclick="this.parentElement.parentElement.parentElement.remove()">×</button></div>' +

            '<div class="form-group"><label for="toName">' + _esc(_tt('tourn.f.name', 'Name')) + ' *</label>' +
            '<input type="text" id="toName" class="form-control" maxlength="120" placeholder="' + _attr(_tt('tourn.f.namePh', 'e.g. Spring Cup 2026')) + '"></div>' +

            '<div class="form-group"><label for="toGameSearch">' + _esc(_tt('tourn.f.game', 'Game')) + '</label>' +
              '<input type="hidden" id="toGameId"><input type="hidden" id="toGameTitle">' +
              '<div id="toGamePicker">' +
                '<div style="display:flex; gap:8px; align-items:stretch;">' +
                  '<input type="text" id="toGameSearch" class="form-control" autocomplete="off" placeholder="' + _attr(_tt('tourn.f.pickGame', 'pick a game')) + '" oninput="_toGameSearchLive()" onfocus="_toGameSearchLive()" style="flex:1 1 auto; min-width:0;">' +
                  '<button type="button" class="tab-head-btn accent" style="flex:none; align-self:stretch; height:auto; width:46px;" onclick="_toAddGame()" title="' + _attr(_tt('tourn.f.addGame', 'Add game')) + '" aria-label="' + _attr(_tt('tourn.f.addGame', 'Add game')) + '"><i class="fas fa-plus"></i></button>' +
                '</div>' +
                '<div id="toGameResults" class="sess-game-results"></div>' +
              '</div>' +
              '<div id="toGameSelected" class="sess-game-selected" style="display:none;"></div>' +
            '</div>' +

            '<div class="form-row">' +
              '<div class="form-group"><label for="toFormat">' + _esc(_tt('tourn.f.format', 'Format')) + '</label>' +
              '<select id="toFormat" class="form-control" onchange="_toEditorFormatChange()">' + fmtOpts + '</select></div>' +
            '</div>' +

            // mărimea mesei — pentru round_robin, tables, single_elim (jocurile variază: 2..12 jucători/masă)
            '<div class="form-group" id="toSeatsRow" style="display:none;">' +
              '<label for="toSeats">' + _esc(_tt('tourn.f.seats', 'Players per table')) + '</label>' +
              '<input type="number" id="toSeats" class="form-control" min="2" max="12" value="4" oninput="this.dataset.touched=1">' +
              '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.f.seatsHint', 'Set the table size to match your game (2 = classic 1v1).')) + '</small></div>' +

            // câți avansează per masă — doar la „mese de N” și single-elim
            '<div class="form-group" id="toTopXRow" style="display:none;">' +
              '<label for="toTopX">' + _esc(_tt('tourn.f.topx', 'Advance per table')) + '</label>' +
              '<input type="number" id="toTopX" class="form-control" min="1" placeholder="' + _attr(_tt('tourn.f.topxPh', 'e.g. 2')) + '"></div>' +

            // număr de runde — doar la Swiss (cu default sugerat, recalculat live)
            '<div class="form-group" id="toRoundsRow" style="display:none;">' +
              '<label for="toRounds">' + _esc(_tt('tourn.f.rounds', 'Number of rounds')) + '</label>' +
              '<input type="number" id="toRounds" class="form-control" min="1" max="20" value="3" oninput="this.dataset.touched=1">' +
              '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.f.roundsHint', 'Everyone plays this many rounds; pairings follow the standings.')) + '</small></div>' +

            // împerechere rundă 1 (doar single-elim)
            '<div class="form-group" id="toSeedRow" style="display:none;">' +
              '<label for="toSeeding">' + _esc(_tt('tourn.f.seeding', 'First-round pairing')) + '</label>' +
              '<select id="toSeeding" class="form-control">' +
                '<option value="random">' + _esc(_tt('tourn.f.seedRandom', 'Random shuffle')) + '</option>' +
                '<option value="seed">' + _esc(_tt('tourn.f.seedSeed', 'By seed (1 vs last, …)')) + '</option>' +
              '</select></div>' +

            // legs (doar round robin)
            '<div class="form-group" id="toLegsRow">' +
              '<label for="toLegs">' + _esc(_tt('tourn.f.legs', 'Rounds against each opponent')) + '</label>' +
              '<select id="toLegs" class="form-control">' +
                '<option value="1">' + _esc(_tt('tourn.f.legs1', 'Single (once)')) + '</option>' +
                '<option value="2">' + _esc(_tt('tourn.f.legs2', 'Double (home & away)')) + '</option>' +
              '</select></div>' +

            '<div class="form-row">' +
              '<div class="form-group"><label for="toMinP">' + _esc(_tt('tourn.f.minP', 'Min participants')) + '</label>' +
              '<input type="number" id="toMinP" class="form-control" min="1" value="2"></div>' +
              '<div class="form-group"><label for="toMaxP">' + _esc(_tt('tourn.f.maxP', 'Max participants')) + '</label>' +
              '<input type="number" id="toMaxP" class="form-control" min="1" placeholder="' + _attr(_tt('tourn.f.unlimited', 'unlimited')) + '"></div>' +
            '</div>' +

            '<div class="form-group"><label style="display:flex; align-items:center; gap:8px; font-weight:normal;">' +
              '<input type="checkbox" id="toWaitlist" checked style="flex:0 0 auto;"> ' + _esc(_tt('tourn.f.waitlist', 'Enable waitlist when full')) + '</label></div>' +

            '<div class="form-group"><label style="display:flex; align-items:center; gap:8px; font-weight:normal;">' +
              '<input type="checkbox" id="toSeries" style="flex:0 0 auto;"> ' + _esc(_tt('tourn.f.series', 'Count toward the annual series')) + '</label>' +
              '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.f.seriesHint', 'Its final standings contribute to the yearly club-champion ranking.')) + '</small></div>' +

            '<div class="form-row">' +
              '<div class="form-group"><label for="toDeadline">' + _esc(_tt('tourn.deadline', 'Registration deadline')) + '</label>' +
              '<input type="datetime-local" id="toDeadline" class="form-control"></div>' +
              '<div class="form-group"><label for="toFee">' + _esc(_tt('tourn.fee', 'Entry fee')) + '</label>' +
              '<input type="number" id="toFee" class="form-control" min="0" step="0.01" placeholder="0"></div>' +
            '</div>' +

            '<div class="form-group"><label for="toPrizes">' + _esc(_tt('tourn.prizes', 'Prizes')) + '</label>' +
            '<input type="text" id="toPrizes" class="form-control" placeholder="' + _attr(_tt('tourn.f.prizesPh', 'e.g. Trophy + new game')) + '"></div>' +

            '<div class="form-group"><label style="display:flex; align-items:center; gap:8px; font-weight:normal;">' +
              '<input type="checkbox" id="toFinalPts" style="flex:0 0 auto;" onchange="_toToggleFinalPrizes()"> ' + _esc(_tt('tourn.f.finalPts', 'Award Club Points by final placement')) + '</label>' +
              '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.f.finalPtsHint', 'Off by default — per-round sessions already grant points.')) + '</small>' +
              '<div id="toFinalPrizesWrap" style="display:none; margin-top:8px;">' +
                '<label for="toFinalPrizes" style="font-size:0.85em;">' + _esc(_tt('tourn.f.finalPrizes', 'Prize points per final place (1st, 2nd, 3rd …)')) + '</label>' +
                '<input type="text" id="toFinalPrizes" class="form-control" placeholder="10, 6, 3" value="10, 6, 3">' +
                '<small style="opacity:0.7; display:block; margin-top:4px;">' + _esc(_tt('tourn.f.finalPrizesHint', 'Comma-separated. Places beyond the list get 0.')) + '</small>' +
              '</div></div>' +

            '<button class="btn btn-primary" onclick="saveTournamentFromEditor()"><i class="fas fa-save"></i> ' + _esc(_tt('common.save', 'Save')) + '</button>' +
            '</div>';
        document.body.appendChild(modal);
        // titlul reflectă modul (creare vs editare)
        try {
            var h = modal.querySelector('.modal-header h2');
            if (h) h.innerHTML = '<i class="fas fa-trophy"></i> ' + _esc(isEdit ? _tt('tourn.edit', 'Edit tournament') : _tt('tourn.new', 'New tournament'));
        } catch (e) {}
        _toEditorFormatChange();
        if (isEdit) _toPrefillEditor(existing);
    }

    /* arată/ascunde câmpurile în funcție de format. */
    function _toEditorFormatChange() {
        try {
            var fmt = (document.getElementById('toFormat') || {}).value;
            var seatsRow = document.getElementById('toSeatsRow');
            var topxRow = document.getElementById('toTopXRow');
            var legsRow = document.getElementById('toLegsRow');
            var seedRow = document.getElementById('toSeedRow');
            var seatsEl = document.getElementById('toSeats');

            // mărimea mesei: la round_robin, tables, single_elim (jocurile variază)
            var showSeats = (fmt === 'round_robin' || fmt === 'tables' || fmt === 'single_elim' || fmt === 'swiss');
            if (seatsRow) seatsRow.style.display = showSeats ? '' : 'none';
            // câți avansează: doar unde are sens (nu la round_robin — toți joacă cu toți)
            if (topxRow) topxRow.style.display = (fmt === 'tables' || fmt === 'single_elim') ? '' : 'none';
            // număr de runde: doar Swiss
            var roundsRow = document.getElementById('toRoundsRow');
            if (roundsRow) roundsRow.style.display = (fmt === 'swiss') ? '' : 'none';
            // tur/retur: doar round_robin (se aplică la mese de 2)
            if (legsRow) legsRow.style.display = (fmt === 'round_robin') ? '' : 'none';
            // împerechere rundă 1: la single_elim ȘI swiss
            if (seedRow) seedRow.style.display = (fmt === 'single_elim' || fmt === 'swiss') ? '' : 'none';

            // default sensibil pentru mărimea mesei: 2 la round_robin (clasic 1v1), 4 în rest
            if (seatsEl && !seatsEl.dataset.touched) {
                seatsEl.value = (fmt === 'round_robin') ? 2 : 4;
            }
            // default rezonabil pentru runde Swiss (organizatorul îl poate ajusta)
            var roundsEl = document.getElementById('toRounds');
            if (roundsEl && fmt === 'swiss' && !roundsEl.dataset.touched) {
                roundsEl.value = 4;
            }
        } catch (e) { _tlog('_toEditorFormatChange', e); }
    }

    /* colectează payload + apel RPC. La succes: închide modal + reîncarcă lista. */
    /* ---- Picker de joc pentru editor (search + poze + add), ca la sesiune ---- */
    function _toGameImgCss(u, fel) {
        if (typeof gameImgCss === 'function') return gameImgCss(u, fel);
        return ('' + (u || '')).replace(/["'()\\<>\s]/g, function (m) { return encodeURIComponent(m); });
    }
    function _toGameSearchLive() {
        var box = document.getElementById('toGameResults');
        var inp = document.getElementById('toGameSearch');
        if (!box || !inp) return;
        var q = inp.value.trim().toLowerCase();
        var parts = q ? q.split(/\s+/) : [];
        var cat = (typeof state !== 'undefined' && state.catalog) ? state.catalog : {};
        var list = Object.keys(cat).map(function (name) { return { name: name, g: cat[name] }; });
        if (parts.length) {
            list = list.filter(function (o) {
                var g = o.g || {};
                var hay = (o.name + ' ' + ((g.categories || []).join(' ')) + ' ' + (g.designer || '') + ' ' + (g.publisher || '') + ' ' + (g.edition || '')).toLowerCase();
                return parts.every(function (p) { return hay.indexOf(p) !== -1; });
            });
        }
        list.sort(function (a, b) { return a.name.localeCompare(b.name); });
        list = list.slice(0, 40);
        if (!list.length) {
            box.innerHTML = '<div class="sess-game-empty">' + _esc(_tt('tourn.f.noGames', 'No games found. Use + to add one.')) + '</div>';
            box.classList.add('show');
            return;
        }
        box.innerHTML = list.map(function (o) {
            var g = o.g || {};
            /* ⚡ PERF §54.1: încărcare LENEȘĂ a imaginilor de catalog, ca la selectorul de
               joc de la sesiune și la banda din Dashboard.
               RO: lista arată până la 40 de carduri într-o casetă de 3-4 rânduri; fără
                   amânare, o singură deschidere a selectorului descărca toate cele 40 de
                   imagini. `loading="lazy"` nu se aplică: e fundal CSS, nu `<img>`.
               ⚠ `toLazyBgAttr` e definită în `app-modules-1.js`, alt fișier — de aceea
                   garda `typeof`, cu revenire la comportamentul vechi dacă lipsește.
               EN: up to 40 catalogue images in a 3-4 row box; `loading="lazy"` does not
                   apply to CSS backgrounds. The helper lives in `app-modules-1.js`, hence
                   the `typeof` guard with a fallback to the previous behaviour. */
            var styleImg = (typeof toLazyBgAttr === 'function')
                ? toLazyBgAttr(g.imageUrl, 'thumb')
                : (g.imageUrl ? ' style="background-image:url(\'' + _toGameImgCss(g.imageUrl, 'thumb') + '\');"' : '');
            return '<div class="sess-game-card' + (g.imageUrl ? '' : ' noimg') + '"' + styleImg + ' onclick="_toPickGame(\'' + _attr(o.name) + '\')">' +
                '<div class="sess-game-card-ov"><span class="sess-game-card-title">' + _esc(o.name) + '</span></div></div>';
        }).join('');
        box.classList.add('show');
        /* ⚠ DUPĂ `.show`: cât timp caseta e ascunsă nimic nu „intersectează", deci
           observatorul n-ar declanșa nimic. EN: must run after the box becomes visible. */
        try { if (typeof toObserveLazyBg === 'function') toObserveLazyBg(box); } catch (e) { _tlog('toGameSearchLive', e); }
    }
    function _toPickGame(name) {
        var g = ((typeof state !== 'undefined' && state.catalog) ? state.catalog : {})[name] || {};
        var idEl = document.getElementById('toGameId');
        var titleEl = document.getElementById('toGameTitle');
        if (idEl) idEl.value = (g.dbId != null ? g.dbId : '');
        if (titleEl) titleEl.value = name;
        var sel = document.getElementById('toGameSelected');
        var picker = document.getElementById('toGamePicker');
        var box = document.getElementById('toGameResults');
        if (box) { box.innerHTML = ''; box.classList.remove('show'); }
        if (sel) {
            var styleImg = g.imageUrl ? ' style="background-image:url(\'' + _toGameImgCss(g.imageUrl, 'wide') + '\');"' : '';
            sel.innerHTML = '<div class="sess-game-selected-card' + (g.imageUrl ? '' : ' noimg') + '"' + styleImg + '>' +
                '<span class="sess-game-selected-title">' + _esc(name) + '</span>' +
                '<button type="button" class="sess-game-clear" onclick="_toClearGame()" aria-label="' + _attr(_tt('common.cancel', 'Cancel')) + '"><i class="fas fa-times"></i></button></div>';
            sel.style.display = 'block';
        }
        if (picker) picker.style.display = 'none';
    }
    function _toClearGame() {
        var idEl = document.getElementById('toGameId');
        var titleEl = document.getElementById('toGameTitle');
        if (idEl) idEl.value = '';
        if (titleEl) titleEl.value = '';
        var sel = document.getElementById('toGameSelected');
        var picker = document.getElementById('toGamePicker');
        var search = document.getElementById('toGameSearch');
        var box = document.getElementById('toGameResults');
        if (sel) { sel.innerHTML = ''; sel.style.display = 'none'; }
        if (picker) picker.style.display = '';
        if (search) search.value = '';
        if (box) { box.innerHTML = ''; box.classList.remove('show'); }
    }
    function _toAddGame() {
        try {
            if (typeof openAddGameModal === 'function') { openAddGameModal(); return; }
            if (typeof openAddGameFromSession === 'function') { openAddGameFromSession(); return; }
            if (typeof showNotification === 'function') showNotification(_tt('tourn.f.addGameHint', 'Add the game from the Game Catalog, then search here.'), 'info');
        } catch (e) { _tlog('_toAddGame', e); }
    }

    /* arată/ascunde câmpul de premii per loc când se bifează bonusul final. */
    function _toToggleFinalPrizes() {
        var wrap = document.getElementById('toFinalPrizesWrap');
        var chk = document.getElementById('toFinalPts');
        if (wrap && chk) wrap.style.display = chk.checked ? 'block' : 'none';
    }

    async function saveTournamentFromEditor() {
        var modal = document.querySelector('.modal.active[data-tourn-editor]');
        if (!modal) return;
        function val(id) { var el = document.getElementById(id); return el ? el.value : ''; }
        function num(id) { var v = val(id); return v === '' ? null : Number(v); }

        var name = (val('toName') || '').trim();
        if (!name) { if (typeof showNotification === 'function') showNotification(_tt('tourn.err.NAME_REQUIRED', 'Name required.'), 'error'); return; }

        var gameId = val('toGameId') || null;
        var gameTitle = val('toGameTitle') || '';

        var fmt = val('toFormat') || 'round_robin';
        var settings = {};
        if (!!(document.getElementById('toSeries') || {}).checked) settings.series = true;
        if (fmt === 'round_robin') settings.legs = (val('toLegs') === '2') ? 2 : 1;
        if (fmt === 'single_elim') settings.seeding = (val('toSeeding') === 'seed') ? 'seed' : 'random';
        if (fmt === 'swiss') {
            settings.seeding = (val('toSeeding') === 'seed') ? 'seed' : 'random';
            settings.rounds = Math.max(1, Math.min(20, num('toRounds') || 4));
        }

        // mărimea mesei se folosește la round_robin, tables, single_elim, swiss
        var usesSeats = (fmt === 'round_robin' || fmt === 'tables' || fmt === 'single_elim' || fmt === 'swiss');
        // avansarea (top_x) doar la tables + single_elim (la RR/Swiss toți joacă toate rundele)
        var usesTopX = (fmt === 'tables' || fmt === 'single_elim');

        var deadlineRaw = val('toDeadline');
        var deadlineIso = deadlineRaw ? new Date(deadlineRaw).toISOString() : null;

        var payload = {
            name: name,
            game_id: gameId,
            game_title: gameTitle,
            format: fmt,
            settings: settings,
            seats_per_table: usesSeats ? (num('toSeats') || (fmt === 'round_robin' ? 2 : 4)) : 2,
            top_x_advance: usesTopX ? num('toTopX') : null,
            min_participants: num('toMinP') || 2,
            max_participants: num('toMaxP'),
            waitlist_enabled: !!(document.getElementById('toWaitlist') || {}).checked,
            registration_deadline: deadlineIso,
            entry_fee: num('toFee'),
            prizes: (val('toPrizes') || '').trim() ? { text: (val('toPrizes') || '').trim() } : {},
            final_points_enabled: !!(document.getElementById('toFinalPts') || {}).checked,
            final_points_template: (function () {
                if (!(document.getElementById('toFinalPts') || {}).checked) return null;
                var raw = (val('toFinalPrizes') || '').trim();
                var arr = raw.split(',').map(function (x) { return Number(x.trim()); }).filter(function (n) { return !isNaN(n); });
                return arr.length ? { prizes: arr } : { prizes: [10, 6, 3] };
            })()
        };

        var res;
        if (_toEditingId) {
            res = await toUpdateTournament(_toEditingId, payload);
        } else {
            res = await toCreateTournament(payload);
        }
        if (!res) return; // toast-ul de eroare a fost deja arătat
        _toEditingId = null;
        modal.remove();
        if (typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
    }

    /* id-ul turneului în curs de editare (null = creare). */
    var _toEditingId = null;

    /* precompletează câmpurile editorului dintr-un turneu existent (mod edit). */
    function _toPrefillEditor(t) {
        if (!t) return;
        function set(id, v) { var el = document.getElementById(id); if (el != null && v != null) el.value = v; }
        function chk(id, v) { var el = document.getElementById(id); if (el) el.checked = !!v; }
        set('toName', t.name || '');
        // joc: preselectăm în picker — găsim titlul din catalog după game_id sau după game_title
        var cat = (typeof state !== 'undefined' && state.catalog) ? state.catalog : {};
        var pickName = null;
        if (t.game_id != null) {
            Object.keys(cat).some(function (nm) { if (String(cat[nm] && cat[nm].dbId) === String(t.game_id)) { pickName = nm; return true; } return false; });
        }
        if (!pickName && t.game_title && cat[t.game_title]) pickName = t.game_title;
        if (pickName) { _toPickGame(pickName); }
        else if (t.game_title) {
            // jocul nu mai e în catalog: arătăm titlul salvat ca selecție (fără dbId)
            var titleEl = document.getElementById('toGameTitle'); if (titleEl) titleEl.value = t.game_title;
            var sel = document.getElementById('toGameSelected'); var picker = document.getElementById('toGamePicker');
            if (sel) { sel.innerHTML = '<div class="sess-game-selected-card noimg"><span class="sess-game-selected-title">' + _esc(t.game_title) + '</span><button type="button" class="sess-game-clear" onclick="_toClearGame()"><i class="fas fa-times"></i></button></div>'; sel.style.display = 'block'; }
            if (picker) picker.style.display = 'none';
        }
        set('toFormat', t.format || 'round_robin');
        var s = t.settings || {};
        if (s.legs === 2) set('toLegs', '2'); else set('toLegs', '1');
        if (s.seeding) set('toSeeding', s.seeding);
        if (s.rounds) { set('toRounds', s.rounds); var re = document.getElementById('toRounds'); if (re) re.dataset.touched = 1; }
        if (t.seats_per_table != null) { set('toSeats', t.seats_per_table); var se = document.getElementById('toSeats'); if (se) se.dataset.touched = 1; }
        if (t.top_x_advance != null) set('toTopX', t.top_x_advance);
        if (t.min_participants != null) set('toMinP', t.min_participants);
        if (t.max_participants != null) set('toMaxP', t.max_participants);
        chk('toWaitlist', t.waitlist_enabled);
        chk('toSeries', s.series);
        if (t.registration_deadline) {
            try { set('toDeadline', new Date(t.registration_deadline).toISOString().slice(0, 16)); } catch (e) {}
        }
        if (t.entry_fee != null) set('toFee', t.entry_fee);
        if (t.prizes && t.prizes.text) set('toPrizes', t.prizes.text);
        chk('toFinalPts', t.final_points_enabled);
        if (t.final_points_template && Array.isArray(t.final_points_template.prizes)) {
            set('toFinalPrizes', t.final_points_template.prizes.join(', '));
        }
        _toEditorFormatChange();
        _toToggleFinalPrizes();
    }

    /* ---- Realtime: reîmprospătează lista ȘI pagina de turneu deschisă ---- */
    var _tournChannel = null;
    function subscribeRealtimeTournaments() {
        if (!_sbReady()) return;
        try {
            if (_tournChannel) { try { sb.removeChannel(_tournChannel); } catch (e) {} _tournChannel = null; }
            var refresh = function () {
                try {
                    // 1) lista, dacă suntem pe tab
                    if (typeof currentTab !== 'undefined' && currentTab === 'tournaments') renderTournaments();
                    // 2) pagina de turneu, dacă un modal e deschis (device B stă pe detaliu)
                    var openPage = document.querySelector('.modal.active[data-tourn-page]');
                    if (openPage && _toCurrentPageId) _renderTournamentPageBody(_toCurrentPageId);
                } catch (e) { _tlog('rtTournaments.refresh', e); }
            };
            _tournChannel = sb.channel('public:tournaments')
                .on('postgres_changes', { event: '*', schema: 'public', table: 'tournaments' }, refresh)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_participants' }, refresh)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_rounds' }, refresh)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_tables' }, refresh)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_seats' }, refresh)
                .subscribe();
        } catch (e) { _tlog('subscribeRealtimeTournaments', e); }
    }


    /* ---- Self-test pentru maparea de erori (pur, rulabil în Node) ---- */
    function _selfTestErrors() {
        var res = [];
        function ck(name, cond, got) { res.push({ test: name, pass: !!cond, got: got }); }
        ck('_errCode string direct', _errCode('FORBIDDEN') === 'FORBIDDEN', _errCode('FORBIDDEN'));
        ck('_errCode din {message}', _errCode({ message: 'TOURNAMENT_FULL' }) === 'TOURNAMENT_FULL', _errCode({ message: 'TOURNAMENT_FULL' }));
        ck('_errCode cu prefix', _errCode({ message: 'PLAYER_NOT_PARTICIPANT: extra' }) === 'PLAYER_NOT_PARTICIPANT', _errCode({ message: 'PLAYER_NOT_PARTICIPANT: extra' }));
        ck('_errCode necunoscut -> gol', _errCode({ message: 'random db glitch' }) === '', _errCode({ message: 'random db glitch' }));
        ck('_errCode null -> gol', _errCode(null) === '', _errCode(null));
        ck('caps inregistrate', (typeof CAP_MIN_RANK === 'undefined') || (CAP_MIN_RANK['tournament.manage'] === 2 && CAP_MIN_RANK['tournament.join'] === 1), 'ok');
        var passed = res.filter(function (r) { return r.pass; }).length;
        try {
            console.log('%c[tournaments] error-map self-test: ' + passed + '/' + res.length + ' PASS',
                'color:' + (passed === res.length ? '#2ecc71' : '#e74c3c') + ';font-weight:bold');
            res.forEach(function (r) { console.log((r.pass ? '  ✓ ' : '  ✗ ') + r.test, r.pass ? '' : r.got); });
        } catch (e) {}
        return { passed: passed, total: res.length, results: res };
    }

    /* ---------- Export global (scope partajat, fără module) ---------- */
    var TO_TOURN = {
        stamp: STAMP,
        // motoare pure
        generateRoundRobin1v1: generateRoundRobin1v1,
        generateRoundRobinTables: generateRoundRobinTables,
        generateElimRound: generateElimRound,
        generateSwissRound: generateSwissRound,
        validatePairing: validatePairing,
        // caller-e RPC
        create: toCreateTournament,
        update: toUpdateTournament,
        setStatus: toSetTournamentStatus,
        join: toJoinTournament,
        leave: toLeaveTournament,
        applyPairing: toApplyPairing,
        reportTable: toReportTable,
        finalize: toFinalize,
        reopen: toReopen,
        advanceElim: toAdvanceElim,
        swissState: toSwissState,
        seriesStandings: toSeriesStandings,
        // loadere
        list: toLoadTournaments,
        load: toLoadTournament,
        // UI
        render: renderTournaments,
        openPage: openTournamentPage,
        openEditor: openTournamentEditor,
        save: saveTournamentFromEditor,
        subscribeRealtime: subscribeRealtimeTournaments,
        // helpere afișare
        formatLabel: toFormatLabel,
        statusLabel: toStatusLabel,
        // teste + intern
        _selfTest: _selfTest,
        _selfTestErrors: _selfTestErrors,
        _selfTestElim: _selfTestElim,
        _selfTestSwiss: _selfTestSwiss,
        _errCode: _errCode,
        _tErrMsg: _tErrMsg,
        _util: { shuffle: _shuffle, mulberry32: _mulberry32 }
    };

    window.TO_TOURN = TO_TOURN;
    // alias-uri directe (pentru handlere/RPC callers viitoare)
    window.toGenerateRoundRobin1v1 = generateRoundRobin1v1;
    window.toGenerateRoundRobinTables = generateRoundRobinTables;
    window.toValidatePairing = validatePairing;
    window.toCreateTournament = toCreateTournament;
    window.toUpdateTournament = toUpdateTournament;
    window.toDeleteTournament = toDeleteTournament;
    window._toMakePublic = _toMakePublic;
    window._toMakePrivate = _toMakePrivate;
    window._toCopyPublicLink = _toCopyPublicLink;
    window._toEditFromCard = _toEditFromCard;
    window._toDeleteFromCard = _toDeleteFromCard;
    window._toToggleSearchPanel = _toToggleSearchPanel;
    window._toSearchInput = _toSearchInput;
    window._toExport = _toExport;
    window.toSetTournamentStatus = toSetTournamentStatus;
    window.toJoinTournament = toJoinTournament;
    window.toLeaveTournament = toLeaveTournament;
    window.toApplyPairing = toApplyPairing;
    window.toLoadTournaments = toLoadTournaments;
    window.toLoadTournament = toLoadTournament;
    window.renderTournaments = renderTournaments;
    window.openTournamentPage = openTournamentPage;
    window.openTournamentEditor = openTournamentEditor;
    window.subscribeRealtimeTournaments = subscribeRealtimeTournaments;
    window.saveTournamentFromEditor = saveTournamentFromEditor;
    window._toToggleFinalPrizes = _toToggleFinalPrizes;
    window._toEditorFormatChange = _toEditorFormatChange;
    window._toGameSearchLive = _toGameSearchLive;
    window._toPickGame = _toPickGame;
    window._toClearGame = _toClearGame;
    window._toAddGame = _toAddGame;
    window._toJoinFromPage = _toJoinFromPage;
    window._toLeaveFromPage = _toLeaveFromPage;
    window._toSetStatusFromPage = _toSetStatusFromPage;
    window._toGenerateBracketFromPage = _toGenerateBracketFromPage;
    window.closeTournamentPage = closeTournamentPage;
    window._toRetryPage = _toRetryPage;
    window._toOpenReport = _toOpenReport;
    window._toSaveReport = _toSaveReport;
    window.setToStandView = setToStandView;
    window.buildTournamentShareData = buildTournamentShareData;
    window._toRenderTieBar = _toRenderTieBar;
    window._toToggleTieBreak = _toToggleTieBreak;
    window._toOpenFinalize = _toOpenFinalize;
    window._toPickWinner = _toPickWinner;
    window._toConfirmFinalize = _toConfirmFinalize;
    window._toReopenFromPage = _toReopenFromPage;
    window._toAdvanceElimFromPage = _toAdvanceElimFromPage;
    window._toAdvanceSwissFromPage = _toAdvanceSwissFromPage;
    window._toToggleNoShow = _toToggleNoShow;
    window.openSeriesStandings = openSeriesStandings;
    window._toSeriesSetYear = _toSeriesSetYear;
    window._toSetSeriesView = _toSetSeriesView;
    window._toOpenScaleEditor = _toOpenScaleEditor;
    window._toSaveScale = _toSaveScale;
    window.injectTournamentStats = injectTournamentStats;
    window._toOpenProfile = _toOpenProfile;

    /* v9 8.3: butonul „Turneu nou" (index.html) pornește dezactivat: fișierul se încarcă
       la cerere, iar un clic înainte ar fi chemat o funcție încă inexistentă.
       EN: the static "New tournament" button is enabled once this file is loaded. */
    try { var _toNewBtn = document.getElementById('newTournamentBtn'); if (_toNewBtn) _toNewBtn.disabled = false; } catch (e) { _tlog('newBtn', e); }

    /* Auto-close: închide modalele de turneu când utilizatorul intră în ALT tab din
       APLICAȚIE (nu când schimbă tabul din browser). Legăm de switchTab. Astfel,
       dacă schimbi tabul din browser cât ești pe tab-ul Turnee, modalul rămâne deschis. */
    (function _installTournAutoClose() {
        if (window._toAutoCloseInstalled) return;
        window._toAutoCloseInstalled = true;
        var SEL = '.modal.active[data-tourn-page], .modal.active[data-to-report], ' +
                  '.modal.active[data-tourn-editor], .modal.active[data-series-page], ' +
                  '.modal.active[data-scale-editor]';
        function closeAll() {
            try {
                var nodes = document.querySelectorAll(SEL);
                for (var i = 0; i < nodes.length; i++) nodes[i].remove();
                _toCurrentPageId = null;
            } catch (e) {}
        }
        /* v9 8.3: închidem DUPĂ ce tabul chiar s-a schimbat, nu înainte. Încărcat la
           cerere, acest împachetător ajunge în EXTERIORUL celui din nav.js (garda
           „modificări nesalvate" din profil); închizând înainte, o navigare anulată din
           dialog ar fi închis totuși modalul. Ordinea împachetătorilor nu mai contează.
           EN: close after the switch really happened, so wrapper order is irrelevant. */
        function curTab() { try { return (typeof currentTab !== 'undefined') ? currentTab : null; } catch (e) { return null; } }
        function installTabHook() {
            if (window._toTabHookInstalled) return true;
            if (typeof window.switchTab !== 'function') return false;
            var orig = window.switchTab;
            window.switchTab = function (tabName) {
                var before = curTab();
                var r = orig.apply(this, arguments);
                try {
                    var after = curTab();
                    if (tabName && after !== 'tournaments' && (after === tabName || after !== before)) closeAll();
                } catch (e) {}
                return r;
            };
            window._toTabHookInstalled = true;
            return true;
        }
        if (!installTabHook()) {
            var tries = 0;
            var iv = setInterval(function () { if (installTabHook() || ++tries > 20) clearInterval(iv); }, 150);
        }
    })();

})();
